import {
  DEFAULT_PROP_SIMULATION_CONFIG,
} from "./PropSimulation";
import {
  getPropGraphStrength,
} from "./PropGraphUtils";

const BOARD_COLUMNS = 5;
const BOARD_ROWS = 4;
const BOARD_SIZE = BOARD_COLUMNS * BOARD_ROWS;
export const PROP_SIMULATION_COMMISSION_TURNS = [5, 6, 7, 8, 9, 10];
const COMMISSION_TURNS = PROP_SIMULATION_COMMISSION_TURNS;
const RARITY_WEIGHTS = [
  { 1: 1000, 2: 0, 3: 0, 4: 0 },
  { 1: 900, 2: 100, 3: 0, 4: 0 },
  { 1: 780, 2: 200, 3: 20, 4: 0 },
  { 1: 650, 2: 300, 3: 40, 4: 10 },
  { 1: 500, 2: 400, 3: 80, 4: 20 },
  { 1: 300, 2: 400, 3: 160, 4: 40 },
];
const JUNK_TAG = 99;
const CRAFT_TAG = 7;
const ANIMAL_TAG = 8;
const INITIAL_MATERIAL_COUNT = 5;
const MAX_SUPPORT_COUNT = 6;
const MAX_RECIPE_COUNT = 6;
const DEFAULT_FRAME_TIME_BUDGET_MS = 8;
const DEFAULT_PERFORMANCE_SAMPLE_RATE = 50;
const MAX_COMMISSION_EXTRA_TURNS = 10;
const PERFORMANCE_STAGE_LABELS = {
  setup: "开局与体系",
  initialLibrary: "初始材料库",
  scopeStats: "统计范围",
  drawBoard: "抽取摆盘",
  presenceStats: "登场统计",
  heldEvents: "持有触发",
  supportEvents: "辅料回合事件",
  supportUse: "辅料使用",
  recipeEvents: "配方事件",
  boardEvents: "原料事件",
  baseYield: "基础产出",
  materialChoice: "原料选择",
  pruneLibrary: "材料库精简",
  supportChoice: "辅料选择",
  libraryStats: "材料库统计",
  recipeChoice: "配方选择",
};

const VALUE_COMPONENT_KEYS = [
  "direct",
  "directMana",
  "stateDelta",
  "growth",
  "growDelta",
  "condition",
  "spawn",
  "spawnDelta",
  "eliminate",
  "eliminateDelta",
  "transform",
  "transformDelta",
  "defense",
  "protectDelta",
  "purifyDelta",
  "accelerateDelta",
  "weight",
  "rarityWeightDelta",
  "pool",
  "synergy",
  "cost",
];
const STATE_DELTA_COMPONENT_KEYS = new Set([
  "spawnDelta",
  "eliminateDelta",
  "transformDelta",
  "growDelta",
  "accelerateDelta",
  "protectDelta",
  "purifyDelta",
  "rarityWeightDelta",
  "stateDelta",
]);
const MIRROR_VALUE_COMPONENT_KEYS = new Set(["directMana", "stateDelta"]);
const VALUE_CONFIDENCE_PRIOR_BY_RARITY = {
  1: 200,
  2: 300,
  3: 500,
  4: 800,
};
const EFFECT_EVENT_SAMPLE_WEIGHT = 4;
const STRENGTH_COEFFICIENT_ZERO_EPSILON = 0.0001;

let nextInstanceId = 1;

export function normalizePropSimulationConfig(config) {
  const merged = {
    ...DEFAULT_PROP_SIMULATION_CONFIG,
    ...config,
    iaaProfiles: config?.iaaProfiles?.length
      ? config.iaaProfiles
      : DEFAULT_PROP_SIMULATION_CONFIG.iaaProfiles,
  };
  const profiles = merged.iaaProfiles.map((profile) => ({
    ...profile,
    percent: Math.max(0, Number(profile.percent || 0)),
  }));
  const totalPercent = profiles.reduce((sum, profile) => sum + profile.percent, 0);
  return {
    ...merged,
    games: Math.max(1, Math.floor(Number(merged.games || 1))),
    seed: Math.floor(Number(merged.seed || DEFAULT_PROP_SIMULATION_CONFIG.seed)),
    frameGameBudget: Math.max(1, Math.floor(Number(merged.frameGameBudget || 1))),
    gameIndexOffset: Math.max(0, Math.floor(Number(merged.gameIndexOffset || 0))),
    performanceSampleRate: Math.max(1, Math.floor(Number(merged.performanceSampleRate || DEFAULT_PERFORMANCE_SAMPLE_RATE))),
    iaaProfiles: totalPercent > 0
      ? profiles
      : DEFAULT_PROP_SIMULATION_CONFIG.iaaProfiles,
  };
}

export function createPropSimulationSession(input, config) {
  const normalizedConfig = normalizePropSimulationConfig(config);
  const data = prepareSimulationData(input);
  const stats = createStatsMap(data.props);
  const scopeStats = new Map();
  const profileStats = new Map(normalizedConfig.iaaProfiles.map((profile) => [profile.key, {
    key: profile.key,
    name: profile.name,
    games: 0,
    totalMagic: 0,
  }]));
  const profiler = createPerformanceProfiler(normalizedConfig.performanceSampleRate);
  const rng = createRng(normalizedConfig.seed);
  const startedAt = Date.now();
  let completedGames = 0;
  let totalMagic = 0;
  const commissionTotals = new Array(COMMISSION_TURNS.length).fill(0);

  return {
    get completedGames() {
      return completedGames;
    },
    get progress() {
      return completedGames / normalizedConfig.games;
    },
    runFrame(frameBudget, frameTimeBudgetMs = DEFAULT_FRAME_TIME_BUDGET_MS) {
      const budget = Math.max(1, Math.floor(Number(frameBudget || normalizedConfig.frameGameBudget)));
      const target = Math.min(normalizedConfig.games, completedGames + budget);
      const frameStartedAt = getNow();
      while (
        completedGames < target &&
        (completedGames === 0 || getNow() - frameStartedAt < frameTimeBudgetMs)
      ) {
        const gameIndex = normalizedConfig.gameIndexOffset + completedGames;
        const profile = pickIaaProfile(normalizedConfig.iaaProfiles, gameIndex);
        const gameProfiler = shouldSamplePerformance(profiler, gameIndex) ? profiler : undefined;
        const gameResult = simulateGame(rng, gameIndex, profile, data, stats, scopeStats, gameProfiler);
        totalMagic += gameResult.magic;
        gameResult.commissionMagic.forEach((value, index) => {
          commissionTotals[index] = (commissionTotals[index] || 0) + value;
        });
        const profileStat = profileStats.get(profile.key);
        if (profileStat) {
          profileStat.games += 1;
          profileStat.totalMagic += gameResult.magic;
        }
        completedGames += 1;
      }
      const done = completedGames >= normalizedConfig.games;
      return {
        done,
        progress: completedGames / normalizedConfig.games,
        completedGames,
        result: done
          ? buildSimulationResult(
            normalizedConfig,
            data,
            stats,
            profileStats,
            totalMagic,
            commissionTotals,
            scopeStats,
            Date.now() - startedAt,
            profiler,
          )
          : undefined,
      };
    },
  };
}

export function runPropSimulationAsync(input, config, onProgress, shouldStop) {
  const session = createPropSimulationSession(input, config);
  return new Promise((resolve, reject) => {
    const run = () => {
      try {
        if (shouldStop?.()) {
          resolve(undefined);
          return;
        }
        const frame = session.runFrame();
        onProgress?.({
          progress: frame.progress,
          completedGames: frame.completedGames,
          totalGames: normalizePropSimulationConfig(config).games,
        });
        if (frame.done) {
          resolve(frame.result);
          return;
        }
        setTimeout(run, 0);
      } catch (error) {
        reject(error);
      }
    };
    setTimeout(run, 0);
  });
}

export function mergePropSimulationResults(results, config, elapsedMs) {
  const validResults = (results || []).filter(Boolean);
  const normalizedConfig = normalizePropSimulationConfig(config);
  if (!validResults.length) return undefined;
  const totalGames = validResults.reduce((sum, result) => sum + Number(result.summary?.games || result.config?.games || 0), 0);
  const totalTurns = validResults.reduce((sum, result) => sum + Number(result.summary?.totalTurns || 0), 0) ||
    totalGames * COMMISSION_TURNS.reduce((sum, value) => sum + value, 0);
  const rows = mergeStatRows(validResults.map((result) => result.rows || []), totalTurns);
  const profileSummaries = mergeProfileSummaries(validResults);
  const statScopes = mergeStatScopes(validResults);
  const summary = buildSummaryFromRows(
    rows,
    totalGames || normalizedConfig.games,
    totalTurns || normalizedConfig.games * COMMISSION_TURNS.reduce((sum, value) => sum + value, 0),
    mergeCommissionAverageMagic(validResults),
    elapsedMs ?? Math.max(...validResults.map((result) => Number(result.summary?.elapsedMs || 0))),
    mergeSummaryMetrics(validResults.map((result) => result.summary)),
    mergePerformanceSummaries(validResults.map((result) => result.summary?.performance || [])),
  );
  return {
    config: {
      ...normalizedConfig,
      games: totalGames || normalizedConfig.games,
      gameIndexOffset: 0,
    },
    strategyText: getPropSimulationStrategyText(normalizedConfig),
    summary,
    profileSummaries,
    typeSummaries: buildTypeSummaries(rows),
    rows,
    statScopes,
  };
}

function createPerformanceProfiler(sampleRate = DEFAULT_PERFORMANCE_SAMPLE_RATE) {
  return {
    sampleRate: Math.max(1, Math.floor(Number(sampleRate || DEFAULT_PERFORMANCE_SAMPLE_RATE))),
    sampledGames: 0,
    stages: new Map(),
  };
}

function shouldSamplePerformance(profiler, gameIndex) {
  if (!profiler) return false;
  const shouldSample = Number(gameIndex || 0) % profiler.sampleRate === 0;
  if (shouldSample) profiler.sampledGames += 1;
  return shouldSample;
}

function measurePerformance(profiler, stage, fn) {
  if (!profiler) return fn();
  const startedAt = getNow();
  try {
    return fn();
  } finally {
    const elapsed = Math.max(0, getNow() - startedAt);
    const item = profiler.stages.get(stage) || { stage, totalMs: 0, samples: 0 };
    item.totalMs += elapsed;
    item.samples += 1;
    profiler.stages.set(stage, item);
  }
}

function buildPerformanceSummary(profiler) {
  if (!profiler) return [];
  const rows = Array.from(profiler.stages.values());
  const totalMs = rows.reduce((sum, row) => sum + Number(row.totalMs || 0), 0);
  return rows
    .map((row) => ({
      stage: row.stage,
      label: PERFORMANCE_STAGE_LABELS[row.stage] || row.stage,
      totalMs: round(row.totalMs || 0, 4),
      averageMs: round((row.totalMs || 0) / Math.max(1, row.samples || profiler.sampledGames || 1), 4),
      percent: round(totalMs ? (row.totalMs || 0) / totalMs * 100 : 0, 2),
      samples: row.samples || 0,
    }))
    .sort((a, b) => b.totalMs - a.totalMs);
}

export function getPropSimulationGameRulesText() {
  // eslint-disable-next-line no-unreachable
  // eslint-disable-next-line no-unreachable
  return [
    "每局游戏所有出现的道具都来自本局指定池（选定的3个体系+杂物体系），包括三选一候选、生成、转化和原型选择。",
    "每局游戏选择3个体系，并固定加入杂物体系（tag=99）。",
    "每局完成6个委托，每个委托分别进行5、6、7、8、9、10轮，共45轮。",
    "盘面为5列x4行；每轮从材料库抽取最多20个原料，随机放入盘面进行反应并生产魔力。",
    "每局初始材料库为5个随机普通原料。",
    "每个委托结束后，从3个候选配方中选择1个加入配方区；已有配方不会再次出现在候选项；配方最多拥有6个，获得后持续生效。",
    "每轮结束后，从3个候选原料中选择1个加入材料库，也可以跳过；材料库中的原料可以通过IAA删除。",
    "每轮结束后，可以通过IAA从3个候选辅料中选择1个；辅料为主动使用的一次性道具，最多持有6个。",
    "所有3选1候选项不会出现衍生物，也不会在同一组候选项中出现重复道具。",
    "所有3选1候选项都可以通过IAA刷新，刷新次数不限；刷新后的第3个候选项在抽品质时会忽略普通品质权重。",
    "付费刷新累计10次后，5分钟内免费刷新，次数不限。",
    "道具品质权重按委托推进：委托1仅普通；委托2开始出现少见；委托3开始出现稀有；委托4开始出现罕见；后续委托高品质权重逐步提高。",
  ];
  // eslint-disable-next-line no-unreachable
  return [
    "每局游戏选择3个体系，并固定加入杂物体系（tag=99）。",
    "每局完成6个委托，每个委托分别进行5、6、7、8、9、10轮，共45轮。",
    "盘面为5列x4行；每轮从材料库抽取最多20个原料，随机放入盘面进行反应并生产魔力。",
    "每局初始材料库为5个随机普通原料。",
    "每个委托结束后，从3个候选配方中选择1个加入配方区；已有配方不会再次出现在候选项；配方最多拥有6个，获得后持续生效。",
    "每轮结束后，从3个候选原料中选择1个加入材料库，也可以跳过；材料库中的原料可以通过IAA删除。",
    "每轮结束后，可以通过IAA从3个候选辅料中选择1个；辅料为主动使用的一次性道具，最多持有6个。",
    "所有三选一候选项都可以通过IAA刷新，刷新次数不限；刷新后的第3个候选项在抽品质时会忽略普通品质权重。",
    "付费刷新累计10次后，5分钟内免费刷新，次数不限。",
    "品质权重按委托推进：委托1仅普通；委托2开始出现少见；委托3开始出现稀有；委托4开始出现罕见；后续委托高品质权重逐步提高。",
  ];
}

export function getPropSimulationStrategyText(config = DEFAULT_PROP_SIMULATION_CONFIG) {
  const normalizedConfig = normalizePropSimulationConfig(config);
  return [
    "开局体系选择：先随机选择一个体系A（工艺、杂物、动物除外），再选择与A互动最强的体系B，再选择与B互动最强的体系C，最后固定加入杂物体系。",
    "三选一决策：所有候选项都按“节点估算强度 + 基础产出 + 品质预期 + 当前类型需求”评分，始终选择评分最高项。",
    "材料库控制：付费策略会持续评估材料库规模；当材料过多导致核心道具登场概率下降时，优先删除低评分、重复或与当前体系协同弱的原料。",
    "免费玩家：每轮只进行免费原料选择，不购买辅料，不刷新，不主动精简材料库。",
    "平均每轮付费1次：优先购买高评分辅料；辅料已满或材料库过大时，将付费次数用于扩大候选池或精简材料库。",
    "平均每轮付费3次：每轮扩大原料和辅料候选池，委托结束时扩大配方候选池，并更积极地精简低价值原料。",
    "无限付费玩家：把当前委托品质权重允许出现的候选池视为可充分刷新，直接选择评分最高的道具，并保持材料库接近20个高价值核心原料。",
    `付费分层：${normalizedConfig.iaaProfiles.map((item) => `${item.name}${item.percent}%`).join("；")}。`,
    "局内执行：每局6个委托，轮数分别为5、6、7、8、9、10；盘面为5x4；每轮从材料库抽取最多20个原料并执行节点逻辑。",
  ];
  // eslint-disable-next-line no-unreachable
  return [
    "开局体系选择：先随机选择一个体系A（工艺、杂物、动物除外），再选择与A互动最强的体系B，再选择与B互动最强的体系C，最后固定加入杂物体系。",
    "三选一决策：所有候选项都按“节点估算强度 + 基础产出 + 品质预期 + 当前类型需求”评分，始终选择评分最高项，不进行随机决策。",
    "免费玩家：每轮只进行免费原料选择，不购买辅料，不刷新。",
    "平均每轮付费1次：优先购买高评分辅料；辅料已满后，将付费次数用于扩大原料或配方候选池。",
    "平均每轮付费3次：每轮扩大原料和辅料候选池，委托结束时扩大配方候选池，在更大的候选集中选择评分最高道具。",
    "无限付费玩家：把当前委托品质权重允许出现的候选池视为可充分刷新，直接选择评分最高的道具。",
    `付费分层：${normalizedConfig.iaaProfiles.map((item) => `${item.name}${item.percent}%`).join("，")}。`,
    "局内执行：每局6个委托，轮数分别为5、6、7、8、9、10；盘面为5x4；每轮从材料库抽取最多20个原料并执行节点逻辑。",
  ];
}


export function propSimulationRowsToCsv(rows) {
  const headers = [
    ["name", "名称"],
    ["typeName", "类型"],
    ["rarityName", "品质"],
    ["tagNames", "标签"],
    ["finalValue", "平衡强度"],
    ["actualCalculatedStrength", "实际计算快照"],
    ["strengthCoefficient", "已保存系数"],
    ["expectedValue", "预期强度"],
    ["deviation", "偏移量"],
    ["deviationRate", "偏移比例"],
    ["confidenceLow", "置信下限"],
    ["confidenceHigh", "置信上限"],
    ["balanceValue", "观测边际"],
    ["triggerValue", "触发强度"],
    ["poolValue", "池价值"],
    ["components.directMana", "直接魔力"],
    ["components.stateDelta", "状态差分"],
    ["components.growDelta", "成长差分"],
    ["components.spawnDelta", "生成差分"],
    ["components.eliminateDelta", "消除差分"],
    ["components.transformDelta", "转化差分"],
    ["components.protectDelta", "保护差分"],
    ["components.purifyDelta", "净化差分"],
    ["components.rarityWeightDelta", "权重差分"],
    ["synergyValue", "协同价值"],
    ["staticPrior", "静态先验"],
    ["intrinsicContribution", "自身贡献"],
    ["intrinsicGlobalStrength", "自身全局强度"],
    ["intrinsicPresentStrength", "自身登场强度"],
    ["bodyContribution", "承接贡献"],
    ["bodyGlobalStrength", "承接全局强度"],
    ["bodyPresentStrength", "承接登场强度"],
    ["sourceContribution", "来源贡献"],
    ["sourceGlobalStrength", "来源全局强度"],
    ["sourcePresentStrength", "来源登场强度"],
    ["presentRounds", "登场轮数"],
    ["uses", "使用次数"],
  ];
  return [
    headers.map(([, title]) => title).join(","),
    ...rows.map((row) => headers.map(([key]) => {
      const value = key === "tagNames" ? row.tagNames.join("|") : getCsvRowValue(row, key);
      return toCsvCell(typeof value === "number" ? round(value, 4) : value);
    }).join(",")),
  ].join("\n");
}

function getCsvRowValue(row, key) {
  if (!String(key).includes(".")) return row[key];
  return String(key).split(".").reduce((value, part) => value?.[part], row);
}


function prepareSimulationData(input) {
  const props = (input.props || []).map(normalizeProp);
  const tags = (input.tags || []).map(normalizeDictItem);
  const types = (input.types || []).map(normalizeDictItem);
  const rarities = (input.rarities || []).map(normalizeDictItem);
  const strengths = input.strengths || [];
  const indexes = createIndexes(props, strengths);
  const scoreCache = new Map();
  const staticValueCache = new Map();
  const data = {
    props,
    tags,
    types,
    rarities,
    strengths,
    indexes,
    typeMap: new Map(types.map((item) => [Number(item.value), item.label || String(item.value)])),
    rarityMap: new Map(rarities.map((item) => [Number(item.value), item.label || String(item.value)])),
    tagMap: new Map(tags.map((item) => [Number(item.value), item.label || String(item.value)])),
    scoreCache,
    staticValueCache,
  };
  data.candidateSystemTags = getCandidateSystemTags(data);
  data.systemScores = createSystemScores(data);
  data.systemInteractions = createSystemInteractionMap(data);
  return data;
}

function normalizeProp(raw) {
  const graph = {
    entryNodeIds: raw.graph?.entryNodeIds || [],
    nodes: raw.graph?.nodes || {},
    flows: raw.graph?.flows || [],
    bindings: raw.graph?.bindings || [],
  };
  return {
    ...raw,
    _id: String(raw._id),
    type: Number(raw.type),
    rarity: Number(raw.rarity),
    tags: (raw.tags || []).map(Number),
    yield: Number(raw.yield || 0),
    derived: raw.derived === true,
    graph,
    compiledGraph: compilePropGraph(graph),
  };
}

function compilePropGraph(graph) {
  const nodes = graph.nodes || {};
  const flowMap = new Map();
  const bindingMap = new Map();
  const entryNodesByType = new Map();
  const nodeTypeSet = new Set();
  Object.values(nodes).forEach((node) => {
    if (!node) return;
    nodeTypeSet.add(node.type);
  });
  (graph.flows || []).forEach((flow) => {
    const key = flow.from.nodeId;
    flowMap.set(key, (flowMap.get(key) || []).concat(flow));
  });
  flowMap.forEach((flows) => flows.sort((a, b) => Number(a.order || 0) - Number(b.order || 0)));
  (graph.bindings || []).forEach((binding) => {
    const key = `${binding.target.nodeId}:${binding.target.portId}`;
    bindingMap.set(key, (bindingMap.get(key) || []).concat(binding));
  });
  (graph.entryNodeIds || []).forEach((id) => {
    const node = nodes[id];
    if (!node) return;
    entryNodesByType.set(node.type, (entryNodesByType.get(node.type) || []).concat(node));
  });
  return {
    nodes,
    entryNodeIds: graph.entryNodeIds || [],
    flowMap,
    bindingMap,
    entryNodesByType,
    nodeTypeSet,
  };
}

function normalizeDictItem(item) {
  return {
    ...item,
    value: Number.isFinite(Number(item.value)) ? Number(item.value) : item.value,
    label: item.label || String(item.value),
  };
}

function createIndexes(props, strengths) {
  const byId = new Map(props.map((prop) => [prop._id, prop]));
  const strengthMap = new Map();
  strengths.forEach((row) => {
    const rarity = Number(row.rarity);
    Object.keys(row).forEach((key) => {
      if (key === "_id" || key === "rarity") return;
      strengthMap.set(`${rarity}:${Number(key)}`, Number(row[key] || 0));
    });
  });
  return { byId, strengthMap };
}

function expectedStrength(prop, data) {
  return data.indexes.strengthMap.get(`${Number(prop.rarity)}:${Number(prop.type)}`) || 0;
}

function staticStrengthValue(prop, data) {
  if (!prop || !data) return 0;
  const id = String(prop._id);
  if (data.staticValueCache?.has(id)) return data.staticValueCache.get(id);
  let value = 0;
  try {
    value = getPropGraphStrength(prop, {
      props: data.props,
      strengths: data.strengths,
      tags: data.tags,
      types: data.types,
      rarities: data.rarities,
    }).value;
  } catch (_) {
    value = 0;
  }
  data.staticValueCache?.set(id, value);
  return value;
}

function decisionScore(prop, data) {
  const id = String(prop._id);
  if (data.scoreCache.has(id)) return data.scoreCache.get(id);
  const graphStrength = staticStrengthValue(prop, data);
  const typeWeight = prop.type === 3 ? 1.15 : prop.type === 2 ? 1.05 : 1;
  const score = (
    graphStrength * 1.8 +
    expectedStrength(prop, data) +
    Math.max(0, Number(prop.yield || 0)) * 2
  ) * typeWeight;
  data.scoreCache.set(id, score);
  return score;
}

function getCandidateSystemTags(data) {
  return data.tags
    .map((tag) => Number(tag.value))
    .filter((tag) => Number.isFinite(tag) && !isExcludedRandomSystemTag(tag, data));
}

function isExcludedRandomSystemTag(tag, data) {
  const label = String(data.tagMap.get(Number(tag)) || "");
  return (
    Number(tag) === JUNK_TAG ||
    Number(tag) === CRAFT_TAG ||
    Number(tag) === ANIMAL_TAG ||
    label.includes("杂物") ||
    label.includes("工艺") ||
    label.includes("动物")
  );
}

function createSystemScores(data) {
  return new Map(data.candidateSystemTags.map((tag) => {
    const props = data.props.filter((prop) => prop.tags.includes(tag) && !prop.derived);
    const top = props
      .slice()
      .sort((a, b) => decisionScore(b, data) - decisionScore(a, data))
      .slice(0, 10);
    const score = top.reduce((sum, prop) => sum + decisionScore(prop, data), 0) / Math.max(1, top.length);
    return [tag, score];
  }));
}

function createSystemInteractionMap(data) {
  const map = new Map();
  data.props.forEach((prop) => {
    const sourceTags = prop.tags.filter((tag) => data.candidateSystemTags.includes(Number(tag)));
    if (!sourceTags.length) return;
    const targetTags = getPropReferencedSystemTags(prop, data);
    if (!targetTags.length) return;
    const weight = Math.max(1, decisionScore(prop, data));
    sourceTags.forEach((sourceTag) => {
      targetTags.forEach((targetTag) => {
        addSystemInteraction(map, Number(sourceTag), Number(targetTag), weight);
        addSystemInteraction(map, Number(targetTag), Number(sourceTag), weight * 0.85);
      });
    });
  });
  return map;
}

function getPropReferencedSystemTags(prop, data) {
  const result = new Set();
  Object.values(prop.graph?.nodes || {}).forEach((node) => {
    if (node?.type === "tagConstant") {
      const tag = Number(node.data?.value);
      if (data.candidateSystemTags.includes(tag)) result.add(tag);
    }
    if (node?.type === "prototypeSelect") {
      const target = data.indexes.byId.get(String(node.data?.prototypeId || ""));
      (target?.tags || []).forEach((tag) => {
        const numericTag = Number(tag);
        if (data.candidateSystemTags.includes(numericTag)) result.add(numericTag);
      });
    }
  });
  return Array.from(result);
}

function addSystemInteraction(map, fromTag, toTag, weight) {
  if (fromTag === toTag) return;
  if (!map.has(fromTag)) map.set(fromTag, new Map());
  const targetMap = map.get(fromTag);
  targetMap.set(toTag, (targetMap.get(toTag) || 0) + weight);
}

function chooseSystemsForGame(rng, data) {
  const candidates = data.candidateSystemTags.length
    ? data.candidateSystemTags
    : data.tags.map((tag) => Number(tag.value)).filter((tag) => Number.isFinite(tag) && tag !== JUNK_TAG);
  const first = sample(rng, candidates);
  const selected = [];
  if (first !== undefined) selected.push(first);
  const second = chooseInteractiveSystem(first, selected, data);
  if (second !== undefined) selected.push(second);
  const third = chooseInteractiveSystem(second ?? first, selected, data);
  if (third !== undefined) selected.push(third);
  return new Set([...selected, JUNK_TAG]);
}

function chooseInteractiveSystem(sourceTag, selectedTags, data) {
  const selected = new Set(selectedTags);
  const candidates = data.candidateSystemTags.filter((tag) => !selected.has(tag));
  if (!candidates.length) return undefined;
  const interactions = data.systemInteractions.get(Number(sourceTag)) || new Map();
  return candidates
    .slice()
    .sort((a, b) =>
      (interactions.get(b) || 0) - (interactions.get(a) || 0) ||
      (data.systemScores.get(b) || 0) - (data.systemScores.get(a) || 0) ||
      a - b,
    )[0];
}

function pickIaaProfile(profiles, gameIndex) {
  const total = profiles.reduce((sum, profile) => sum + Math.max(0, Number(profile.percent || 0)), 0) || 100;
  const cursor = gameIndex % total;
  let accumulated = 0;
  for (const profile of profiles) {
    accumulated += Math.max(0, Number(profile.percent || 0));
    if (cursor < accumulated) return profile;
  }
  return profiles[profiles.length - 1];
}

function createRng(seed) {
  let state = (seed >>> 0) || DEFAULT_PROP_SIMULATION_CONFIG.seed;
  return () => {
    state = (state * 1664525 + 1013904223) >>> 0;
    return state / 0x100000000;
  };
}

function sample(rng, items) {
  if (!items.length) return undefined;
  return items[Math.floor(rng() * items.length)];
}

function shuffle(rng, items) {
  const arr = items.slice();
  for (let index = arr.length - 1; index > 0; index -= 1) {
    const target = Math.floor(rng() * (index + 1));
    [arr[index], arr[target]] = [arr[target], arr[index]];
  }
  return arr;
}

function weightedRarity(rng, commissionIndex, noCommon = false, game) {
  const weights = { ...(RARITY_WEIGHTS[commissionIndex] || RARITY_WEIGHTS[RARITY_WEIGHTS.length - 1]) };
  if (noCommon) weights[1] = 0;
  (game?.rarityWeightModifiers || []).forEach((modifier) => {
    const rarity = Number(modifier.rarity);
    if (Number.isFinite(rarity) && weights[rarity] !== undefined) {
      weights[rarity] = Math.max(0, Number(weights[rarity] || 0) * Math.max(0, Number(modifier.multiplier || 1)));
    }
  });
  const entries = Object.entries(weights).map(([rarity, weight]) => [Number(rarity), Math.max(0, Number(weight))]);
  const total = entries.reduce((sum, [, weight]) => sum + weight, 0);
  if (total <= 0) return 1;
  let cursor = rng() * total;
  for (const [rarity, weight] of entries) {
    cursor -= weight;
    if (cursor <= 0) return rarity;
  }
  return entries[entries.length - 1][0];
}

function buildPools(props, systems) {
  const inSystems = props.filter((prop) => prop.tags.some((tag) => systems.has(tag)));
  const ids = new Set(inSystems.map((prop) => prop._id));
  return {
    all: inSystems,
    ids,
    materials: inSystems.filter((prop) => prop.type === 1 && !prop.derived),
    supports: inSystems.filter((prop) => prop.type === 2 && !prop.derived),
    recipes: inSystems.filter((prop) => prop.type === 3 && !prop.derived),
    prototypes: inSystems.filter((prop) => !prop.derived),
  };
}

function isPropInGamePool(game, prop) {
  if (!prop) return false;
  return game.pools?.ids?.has(String(prop._id)) === true;
}

function getRuntimePropValue(game, prop) {
  if (!prop) return 0;
  const data = game.data;
  const expected = data ? expectedStrength(prop, data) : 0;
  const staticValue = data ? staticStrengthValue(prop, data) : 0;
  const baseYield = Math.max(0, Number(prop.yield || 0));
  return Math.max(expected, staticValue, baseYield);
}

function estimateCurrentMaterialReplacementValue(game) {
  const materials = game.pools?.materials || [];
  if (!materials.length) return 0;
  const weights = RARITY_WEIGHTS[game.currentCommissionIndex] || RARITY_WEIGHTS[0];
  const weightedValues = Object.entries(weights).map(([rarityKey, weight]) => {
    const rarity = Number(rarityKey);
    const rarityMaterials = materials.filter((prop) => Number(prop.rarity) === rarity);
    const pool = rarityMaterials.length ? rarityMaterials : materials;
    const averageValue = pool.reduce((sum, prop) => sum + getRuntimePropValue(game, prop), 0) / Math.max(1, pool.length);
    return {
      weight: Math.max(0, Number(weight || 0)),
      value: averageValue,
    };
  });
  const totalWeight = weightedValues.reduce((sum, item) => sum + item.weight, 0);
  if (!totalWeight) {
    return materials.reduce((sum, prop) => sum + getRuntimePropValue(game, prop), 0) / materials.length;
  }
  return weightedValues.reduce((sum, item) => sum + item.value * item.weight, 0) / totalWeight;
}

function estimateLibraryPressure(game) {
  const size = Math.max(1, game.library?.length || 1);
  const fillRatio = Math.min(1, size / BOARD_SIZE);
  const lateGameRatio = Math.min(1, Math.max(0, Number(game.globalTurn || 1) - 1) / Math.max(1, COMMISSION_TURNS.reduce((sum, value) => sum + value, 0) - 1));
  return 0.65 + fillRatio * 0.2 + lateGameRatio * 0.25;
}

const prototypeFilterReferenceCache = new WeakMap();

function getPrototypeFilterReferenceIds(prop) {
  if (!prop || typeof prop !== "object") return new Set();
  const cached = prototypeFilterReferenceCache.get(prop);
  if (cached) return cached;
  const graph = prop.graph || {};
  const nodes = graph.nodes || {};
  const ids = new Set();
  (graph.bindings || []).forEach((binding) => {
    const targetNode = nodes[binding?.target?.nodeId];
    if (targetNode?.type !== "filterPrototype" || binding?.target?.portId !== "prototype") return;
    const sourceNode = nodes[binding?.source?.nodeId];
    if (sourceNode?.type === "prototypeSelect" && sourceNode.data?.prototypeId) {
      ids.add(String(sourceNode.data.prototypeId));
    }
  });
  prototypeFilterReferenceCache.set(prop, ids);
  return ids;
}

function estimateInstanceSynergyValue(game, inst, runtimeValue) {
  const prototypeId = String(inst?.prop?._id || "");
  if (!prototypeId || Number(inst?.prop?.type) !== 1) return 0;

  const heldInstances = game.__stateValueHeldInstances || getHeldInstances(game);
  const users = heldInstances.filter((other) => (
    other.id !== inst.id &&
    !other.eliminated &&
    getPrototypeFilterReferenceIds(other.prop).has(prototypeId)
  ));
  if (!users.length) return 0;

  const referenceValue = users.reduce((sum, other) => sum + Math.max(0, getRuntimePropValue(game, other.prop)), 0);
  if (referenceValue <= 0) return 0;

  const librarySize = Math.max(1, game.library?.length || 1);
  const availability = isInstanceOnBoard(game, inst) ? 1 : Math.min(1, BOARD_SIZE / librarySize);
  const remainingFactor = Math.max(0.25, Math.min(1, estimateRemainingTurns(game) / 10));
  const ownValue = Math.max(0, Number(runtimeValue || 0));
  return (referenceValue * 0.12 + ownValue * 0.35) * availability * remainingFactor;
}

function estimateGameStateValue(game) {
  const seen = new Set();
  let value = 0;
  const previousHeldInstances = game.__stateValueHeldInstances;
  game.__stateValueHeldInstances = getHeldInstances(game);
  try {
    [game.library, game.board, game.recipes, game.supports].forEach((instances) => {
      (instances || []).forEach((inst) => {
        if (!inst || inst.eliminated || seen.has(inst.id)) return;
        seen.add(inst.id);
        value += estimateInstanceStateValue(game, inst);
      });
    });
    value += estimateRarityWeightStateValue(game);
    value += estimateCommissionExtensionStateValue(game);
    return Number.isFinite(value) ? value : 0;
  } finally {
    if (previousHeldInstances === undefined) delete game.__stateValueHeldInstances;
    else game.__stateValueHeldInstances = previousHeldInstances;
  }
}

function estimateInstanceStateValue(game, inst) {
  const prop = inst?.prop;
  if (!prop) return 0;
  const remainingTurns = Math.max(1, estimateRemainingTurns(game));
  const runtimeValue = getRuntimePropValue(game, prop);
  const currentYield = Number(prop.yield || 0) + Number(inst.yieldBonus || 0);
  const statusValue = estimateInstanceStatusValue(game, inst, runtimeValue);

  if (Number(prop.type) === 1) {
    const librarySize = Math.max(1, game.library?.length || 1);
    const drawProbability = Math.min(1, BOARD_SIZE / librarySize);
    const replacementValue = estimateCurrentMaterialReplacementValue(game);
    const qualityDelta = runtimeValue - replacementValue;
    const futureWindow = Math.min(10, remainingTurns);
    const futureYieldValue = currentYield * drawProbability * futureWindow;
    const libraryQualityValue = qualityDelta * drawProbability * estimateLibraryPressure(game);
    const boardValue = isInstanceOnBoard(game, inst) ? currentYield : 0;
    return futureYieldValue + libraryQualityValue + boardValue + statusValue +
      estimateTimerReadinessValue(game, inst, runtimeValue) +
      estimateInstanceSynergyValue(game, inst, runtimeValue);
  }

  if (Number(prop.type) === 2) {
    const storedValue = Math.max(0, Number(inst.collectedMagic || 0));
    return (inst.used ? 0 : runtimeValue) + storedValue + statusValue;
  }

  if (Number(prop.type) === 3) {
    return runtimeValue * Math.min(10, remainingTurns) * 0.5 + statusValue;
  }

  return runtimeValue + statusValue;
}

function estimateInstanceStatusValue() {
  return 0;
}

function estimateTimerReadinessValue(game, inst, runtimeValue) {
  const entries = getTurnStartEventStateEntries(game, inst);
  if (!entries.length) return 0;
  const base = Math.max(0, Number(runtimeValue || 0)) * 0.08;
  return entries.reduce((sum, [, state]) => {
    const timer = Math.max(0, Number(state?.timer || 0));
    return sum + base / (timer + 1);
  }, 0);
}

function estimateRarityWeightStateValue(game) {
  const modifiers = game.rarityWeightModifiers || [];
  if (!modifiers.length) return 0;
  const baseValue = estimateWeightedPoolValue(game, false);
  const modifiedValue = estimateWeightedPoolValue(game, true);
  const remainingChoices = Math.max(1, estimateRemainingTurns(game) + (COMMISSION_TURNS.length - Number(game.currentCommissionIndex || 0) - 1));
  return (modifiedValue - baseValue) * Math.min(10, remainingChoices) * 0.35;
}

function estimateWeightedPoolValue(game, withModifiers) {
  const pool = [
    ...(game.pools?.materials || []),
    ...(game.pools?.supports || []),
    ...(game.pools?.recipes || []),
  ].filter((prop) => !prop.derived);
  if (!pool.length) return 0;
  const weights = { ...(RARITY_WEIGHTS[game.currentCommissionIndex] || RARITY_WEIGHTS[0]) };
  if (withModifiers) {
    (game.rarityWeightModifiers || []).forEach((modifier) => {
      const rarity = Number(modifier.rarity);
      if (weights[rarity] !== undefined) {
        weights[rarity] = Math.max(0, Number(weights[rarity] || 0) * Math.max(0, Number(modifier.multiplier || 1)));
      }
    });
  }
  const weighted = Object.entries(weights).map(([rarityKey, weight]) => {
    const rarity = Number(rarityKey);
    const rarityProps = pool.filter((prop) => Number(prop.rarity) === rarity);
    const candidates = rarityProps.length ? rarityProps : pool;
    const value = candidates.reduce((sum, prop) => sum + getRuntimePropValue(game, prop), 0) / Math.max(1, candidates.length);
    return { weight: Math.max(0, Number(weight || 0)), value };
  });
  const totalWeight = weighted.reduce((sum, item) => sum + item.weight, 0);
  if (!totalWeight) return pool.reduce((sum, prop) => sum + getRuntimePropValue(game, prop), 0) / pool.length;
  return weighted.reduce((sum, item) => sum + item.value * item.weight, 0) / totalWeight;
}

function estimateCommissionExtensionStateValue(game) {
  const extraTurns = (game.commissionExtraTurns || []).reduce((sum, value) => sum + Math.max(0, Number(value || 0)), 0);
  if (!extraTurns) return 0;
  const averageBoardYield = (game.board || [])
    .filter((inst) => !inst.eliminated)
    .reduce((sum, inst) => sum + Math.max(0, Number(inst.prop?.yield || 0) + Number(inst.yieldBonus || 0)), 0) / Math.max(1, game.board?.length || 0);
  return extraTurns * averageBoardYield;
}

function estimateRemainingTurns(game) {
  const totalTurns = COMMISSION_TURNS.reduce((sum, value) => sum + value, 0) +
    (game.commissionExtraTurns || []).reduce((sum, value) => sum + Math.max(0, Number(value || 0)), 0);
  return Math.max(0, totalTurns - Math.max(0, Number(game.globalTurn || 0)) + 1);
}

function isInstanceOnBoard(game, inst) {
  return (game.board || []).some((item) => item.id === inst?.id && !item.eliminated);
}

function getOfferAttemptCount(profile, baseAttempts) {
  if (profile.key === "optimal") return Number.POSITIVE_INFINITY;
  if (profile.key === "payThree") return baseAttempts + 3;
  if (profile.key === "payOnce") return baseAttempts + 1;
  return baseAttempts;
}

function pickOffer(rng, pool, commissionIndex, data, profile, baseAttempts = 1, excludedIds = new Set(), game) {
  const availablePool = pool.filter((prop) => !prop.derived && !excludedIds.has(prop._id));
  if (!availablePool.length) return undefined;
  const attempts = getOfferAttemptCount(profile, baseAttempts);
  if (!Number.isFinite(attempts)) {
    const allowedRarities = Object.entries(RARITY_WEIGHTS[commissionIndex] || {})
      .filter(([, weight]) => Number(weight) > 0)
      .map(([rarity]) => Number(rarity));
    const allowedPool = availablePool.filter((prop) => allowedRarities.includes(Number(prop.rarity)));
    return chooseBest(allowedPool.length ? allowedPool : availablePool, data);
  }

  const choices = [];
  const choiceIds = new Set();
  for (let attempt = 0; attempt < attempts; attempt += 1) {
    for (let index = 0; index < 3; index += 1) {
      const slotPool = availablePool.filter((prop) => !choiceIds.has(prop._id));
      if (!slotPool.length) break;
      const rarity = weightedRarity(rng, commissionIndex, attempt > 0 && index === 2, game);
      const rarityPool = slotPool.filter((prop) => prop.rarity === rarity);
      const picked = sample(rng, rarityPool.length ? rarityPool : slotPool);
      if (picked) {
        choices.push(picked);
        choiceIds.add(picked._id);
      }
    }
  }
  return chooseBest(choices, data);
}

function chooseBest(items, data) {
  return items
    .slice()
    .sort((a, b) => decisionScore(b, data) - decisionScore(a, data) || String(a._id).localeCompare(String(b._id)))[0];
}

function getTargetLibrarySize(profile) {
  if (profile.key === "free") return Number.POSITIVE_INFINITY;
  if (profile.key === "payOnce") return 24;
  return BOARD_SIZE;
}

function pruneMaterialLibrary(game, profile, data) {
  const targetSize = getTargetLibrarySize(profile);
  if (!Number.isFinite(targetSize) || game.library.length <= targetSize) return 0;
  const removeCount = game.library.length - targetSize;
  const counts = new Map();
  game.library.forEach((inst) => {
    counts.set(inst.prop._id, (counts.get(inst.prop._id) || 0) + 1);
  });
  const removeIndexes = new Set(
    game.library
      .map((inst, index) => ({
        index,
        duplicatePenalty: counts.get(inst.prop._id) > 1 ? 0 : 1,
        score: decisionScore(inst.prop, data),
      }))
      .sort((a, b) => a.duplicatePenalty - b.duplicatePenalty || a.score - b.score || a.index - b.index)
      .slice(0, removeCount)
      .map((item) => item.index),
  );
  const removedInstances = game.library.filter((_, index) => removeIndexes.has(index));
  removedInstances.forEach((inst) => removeInstanceFromGame(game, inst));
  return removedInstances.length;
}

function createInstance(prop, ownerId) {
  return {
    id: `inst_${nextInstanceId++}`,
    prop,
    ownerId: ownerId || prop._id,
    yieldBonus: 0,
    yieldBonusBySource: new Map(),
    yieldBonusByIntrinsic: new Map(),
    collectedMagic: 0,
    protected: false,
    purified: false,
    used: false,
    eliminated: false,
    x: -1,
    y: -1,
  };
}

function addLibraryInstance(game, prop, ownerId) {
  const inst = createInstance(prop, ownerId);
  game.library.push(inst);
  return inst;
}

function addRecipeInstance(game, prop) {
  const inst = createInstance(prop);
  game.recipes.push(inst);
  return inst;
}

function addSupportInstance(game, prop) {
  const inst = createInstance(prop);
  game.supports.push(inst);
  return inst;
}

function makeRuntime(prop, actor, game, sourceId) {
  const compiledGraph = prop.compiledGraph || compilePropGraph(prop.graph || {});
  return {
    prop,
    actor,
    game,
    sourceId: sourceId || prop._id,
    nodes: compiledGraph.nodes,
    entryNodeIds: compiledGraph.entryNodeIds,
    entryNodesByType: compiledGraph.entryNodesByType,
    flowMap: compiledGraph.flowMap,
    bindingMap: compiledGraph.bindingMap,
    visiting: new Set(),
    filterTarget: undefined,
    targetSelectionHint: undefined,
    eventType: undefined,
  };
}

function createStatsMap(props) {
  return new Map(props.map((prop) => [prop._id, createEmptyPropStat(prop)]));
}

function createEmptyPropStat(prop) {
  return {
    prop,
    contribution: 0,
    bodyContribution: 0,
    intrinsicContribution: 0,
    components: createEmptyValueComponents(),
    presentRounds: 0,
    uses: 0,
    effectEvents: 0,
  };
}

function createEmptyValueComponents() {
  return Object.fromEntries(VALUE_COMPONENT_KEYS.map((key) => [key, 0]));
}

function ensureScopeStats(scopeStats, data, key, type, label, meta = {}) {
  if (!scopeStats.has(key)) {
    scopeStats.set(key, {
      key,
      type,
      label,
      meta,
      games: 0,
      turns: 0,
      metrics: createEmptyScopeMetrics(),
      stats: createStatsMap(data.props),
    });
  }
  return scopeStats.get(key);
}

function createEmptyScopeMetrics() {
  return {
    librarySizeSum: 0,
    librarySizeSamples: 0,
    maxLibrarySize: 0,
    prunedMaterialCount: 0,
  };
}

function touchScope(scope, gameIndex, turns = 1) {
  if (!scope) return;
  if (scope.lastGameIndex !== gameIndex) {
    scope.games += 1;
    scope.lastGameIndex = gameIndex;
  }
  scope.turns += turns;
}

function entryNodes(runtime, type) {
  if (runtime.entryNodesByType) return runtime.entryNodesByType.get(type) || [];
  const nodes = runtime.entryNodeIds.map((id) => runtime.nodes[id]).filter(Boolean);
  return nodes.filter((node) => node.type === type);
}

function addContribution(game, propId, value, component = "direct") {
  if (!propId || !Number.isFinite(value) || value === 0) return;
  const stat = game.stats.get(propId);
  if (stat) addStatContribution(stat, value, component);
  for (const scopeKey of game.currentScopeKeys || []) {
    const scopedStat = game.scopeStats?.get(scopeKey)?.stats.get(propId);
    if (scopedStat) addStatContribution(scopedStat, value, component);
  }
}

function addComponentOnly(game, propId, value, component) {
  if (!propId || !Number.isFinite(value) || value === 0) return;
  const stat = game.stats.get(propId);
  if (stat) addStatComponentOnly(stat, value, component);
  for (const scopeKey of game.currentScopeKeys || []) {
    const scopedStat = game.scopeStats?.get(scopeKey)?.stats.get(propId);
    if (scopedStat) addStatComponentOnly(scopedStat, value, component);
  }
}

function addStatContribution(stat, value, component) {
  stat.contribution += value;
  const key = VALUE_COMPONENT_KEYS.includes(component) ? component : "direct";
  stat.components[key] = (stat.components[key] || 0) + value;
  stat.effectEvents = (stat.effectEvents || 0) + 1;
}

function addStatComponentOnly(stat, value, component) {
  const key = VALUE_COMPONENT_KEYS.includes(component) ? component : "direct";
  stat.components[key] = (stat.components[key] || 0) + value;
}

function addStateDeltaContribution(game, propId, value, component = "stateDelta") {
  if (!propId || !Number.isFinite(value) || value === 0) return;
  const key = VALUE_COMPONENT_KEYS.includes(component) ? component : "stateDelta";
  const contributionKey = key === "stateDelta" ? "condition" : key;
  addContribution(game, propId, value, contributionKey);
  if (STATE_DELTA_COMPONENT_KEYS.has(key)) addComponentOnly(game, propId, value, "stateDelta");
}

function addBodyContribution(game, propId, value) {
  if (!propId || !Number.isFinite(value) || value === 0) return;
  const stat = game.stats.get(propId);
  if (stat) stat.bodyContribution += value;
  for (const scopeKey of game.currentScopeKeys || []) {
    const scopedStat = game.scopeStats?.get(scopeKey)?.stats.get(propId);
    if (scopedStat) scopedStat.bodyContribution += value;
  }
}

function addIntrinsicContribution(game, propId, value) {
  if (!propId || !Number.isFinite(value) || value === 0) return;
  const stat = game.stats.get(propId);
  if (stat) stat.intrinsicContribution += value;
  for (const scopeKey of game.currentScopeKeys || []) {
    const scopedStat = game.scopeStats?.get(scopeKey)?.stats.get(propId);
    if (scopedStat) scopedStat.intrinsicContribution += value;
  }
}

function addUse(game, propId) {
  if (!propId) return;
  const stat = game.stats.get(propId);
  if (stat) stat.uses += 1;
  for (const scopeKey of game.currentScopeKeys || []) {
    const scopedStat = game.scopeStats?.get(scopeKey)?.stats.get(propId);
    if (scopedStat) scopedStat.uses += 1;
  }
}

function addPresentRound(game, propId) {
  if (!propId) return;
  const stat = game.stats.get(propId);
  if (stat) stat.presentRounds += 1;
  for (const scopeKey of game.currentScopeKeys || []) {
    const scopedStat = game.scopeStats?.get(scopeKey)?.stats.get(propId);
    if (scopedStat) scopedStat.presentRounds += 1;
  }
}

function addScopeMetric(game, metric, value) {
  for (const scopeKey of game.currentScopeKeys || []) {
    const metrics = game.scopeStats?.get(scopeKey)?.metrics;
    if (metrics) metrics[metric] = (metrics[metric] || 0) + value;
  }
}

function recordLibrarySize(game) {
  const size = game.library.length;
  for (const scopeKey of game.currentScopeKeys || []) {
    const metrics = game.scopeStats?.get(scopeKey)?.metrics;
    if (!metrics) continue;
    metrics.librarySizeSum += size;
    metrics.librarySizeSamples += 1;
    metrics.maxLibrarySize = Math.max(metrics.maxLibrarySize || 0, size);
  }
}

function creditBaseYield(game, inst) {
  if (inst.eliminated) return;
  const base = Number(inst.prop.yield || 0);
  if (base) addContribution(game, inst.ownerId, base, "direct");
  if (base) addComponentOnly(game, inst.ownerId, base, "directMana");
  if (base) addIntrinsicContribution(game, inst.prop._id, base);
  addBodyContribution(game, inst.prop._id, base + inst.yieldBonus);
  game.magic += base + inst.yieldBonus;
}

function resolveInputNodes(runtime, nodeId, portId) {
  return (runtime.bindingMap.get(`${nodeId}:${portId}`) || [])
    .map((binding) => runtime.nodes[binding.source.nodeId])
    .filter(Boolean);
}

function resolveTargets(runtime, nodeId, portId) {
  const sources = resolveInputNodes(runtime, nodeId, portId);
  if (!sources.length) return [];
  return uniqueTargets(sources.flatMap((node) => evalTargetNode(runtime, node)));
}

function uniqueTargets(targets) {
  const seen = new Set();
  return targets.filter((target, index) => {
    const key = getTargetIdentityKey(target, index);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function getTargetIdentityKey(target, index) {
  if (!target) return `empty:${index}`;
  if (target.id !== undefined && target.id !== null) return `instance:${target.id}`;
  const prototypeId = getTargetPrototypeId(target);
  if (prototypeId) return `prototype:${prototypeId}`;
  return `object:${index}`;
}

function evalTargetNode(runtime, node) {
  const game = runtime.game;
  switch (node.type) {
    case "targetSelf":
      if (runtime.filterTarget) return [runtime.filterTarget];
      return runtime.actor ? [runtime.actor] : [];
    case "targetBoard":
      return selectBoardTargets(game, runtime.actor, node.data || {});
    case "prototypeLibrary":
      return getPrototypeLibraryProps(game, node.data || {}).map((prop) => ({ prototype: prop }));
    case "prototypeSelect": {
      const prop = game.indexes.byId.get(String(node.data?.prototypeId || ""));
      return isPropInGamePool(game, prop) ? [{ prototype: prop }] : [];
    }
    case "targetFilter": {
      const targets = resolveTargets(runtime, node.id, "targets");
      return targets.filter((target) => {
        const previousFilterTarget = runtime.filterTarget;
        runtime.filterTarget = target;
        try {
          return evalConditionInput(runtime, node.id, "condition");
        } finally {
          runtime.filterTarget = previousFilterTarget;
        }
      });
    }
    case "filterRarity": {
      const targets = resolveTargets(runtime, node.id, "targets");
      const values = resolveInputNodes(runtime, node.id, "rarity").map((source) => evalValueNode(runtime, source));
      if (!values.length) return targets;
      return targets.filter((target) => values.includes(Number(getTargetProp(target)?.rarity)));
    }
    case "filterTag": {
      const targets = resolveTargets(runtime, node.id, "targets");
      const values = resolveInputNodes(runtime, node.id, "tag").map((source) => evalValueNode(runtime, source));
      if (!values.length) return targets;
      return targets.filter((target) => {
        const prop = getTargetProp(target);
        return prop?.tags?.some((tag) => values.includes(Number(tag)));
      });
    }
    case "filterPrototype": {
      const targets = resolveTargets(runtime, node.id, "targets");
      const prototypeIds = new Set(
        resolveTargets(runtime, node.id, "prototype")
          .map(getTargetPrototypeId)
          .filter(Boolean)
          .map(String),
      );
      if (!prototypeIds.size) return targets;
      return targets.filter((target) => prototypeIds.has(String(getTargetPrototypeId(target) || "")));
    }
    case "filterRandom": {
      const targets = resolveTargets(runtime, node.id, "targets");
      const count = Math.max(0, Math.floor(evalInputNumber(runtime, node.id, "count", 1)));
      if (runtime.targetSelectionHint?.preferPositiveTurnStartTimer) {
        const preferred = shuffle(game.rng, targets.filter((target) => hasPositiveTurnStartTimer(game, target)));
        const fallback = shuffle(game.rng, targets.filter((target) => !hasPositiveTurnStartTimer(game, target)));
        return preferred.concat(fallback).slice(0, count);
      }
      return shuffle(game.rng, targets).slice(0, count);
    }
    default:
      return [];
  }
}

function getPrototypeLibraryProps(game, data) {
  const propType = normalizePrototypeLibraryPropType(data?.propType ?? data?.type);
  return game.pools.prototypes.filter((prop) => Number(prop.type) === propType);
}

function normalizePrototypeLibraryPropType(value) {
  const type = Number(value);
  return type === 2 || type === 3 ? type : 1;
}

function selectBoardTargets(game, actor, data) {
  const live = game.board.filter((item) => !item.eliminated);
  if (!data || data.scope === "all") return live;
  if (data.scope === "absolute") {
    const positions = normalizeBoardTargetPositions(data.position, "corner", ABSOLUTE_BOARD_TARGET_POSITIONS);
    return live.filter((item) => positions.some((position) => matchesAbsolutePosition(item, position)));
  }
  if (data.scope === "relative" && actor && actor.x >= 0) {
    const positions = normalizeBoardTargetPositions(data.position, "adjacent", RELATIVE_BOARD_TARGET_POSITIONS);
    return live.filter((item) =>
      item.id !== actor.id &&
      positions.some((position) => matchesRelativePosition(actor, item, position)),
    );
  }
  return [];
}

const ABSOLUTE_BOARD_TARGET_POSITIONS = new Set(["corner", "edge", "firstRow", "lastRow", "firstColumn", "lastColumn", "middleColumn"]);
const RELATIVE_BOARD_TARGET_POSITIONS = new Set(["adjacent", "sameRow", "sameColumn", "diagonal"]);

function normalizeBoardTargetPositions(position, fallback, allowed) {
  const values = Array.isArray(position) ? position : [position || fallback];
  const normalized = Array.from(new Set(values.map(String).filter((item) => allowed.has(item))));
  return normalized.length ? normalized : [fallback];
}

function matchesAbsolutePosition(item, position) {
  const x = item.x;
  const y = item.y;
  if (position === "corner") return (x === 0 || x === BOARD_COLUMNS - 1) && (y === 0 || y === BOARD_ROWS - 1);
  if (position === "edge") return x === 0 || y === 0 || x === BOARD_COLUMNS - 1 || y === BOARD_ROWS - 1;
  if (position === "firstRow") return y === 0;
  if (position === "lastRow") return y === BOARD_ROWS - 1;
  if (position === "firstColumn") return x === 0;
  if (position === "lastColumn") return x === BOARD_COLUMNS - 1;
  if (position === "middleColumn") return x === Math.floor(BOARD_COLUMNS / 2);
  return false;
}

function matchesRelativePosition(origin, item, position) {
  const dx = Math.abs(origin.x - item.x);
  const dy = Math.abs(origin.y - item.y);
  if (position === "adjacent") return dx + dy === 1;
  if (position === "sameRow") return origin.y === item.y;
  if (position === "sameColumn") return origin.x === item.x;
  if (position === "diagonal") return dx === dy && dx > 0;
  return false;
}

function getTargetProp(target) {
  if (!target) return undefined;
  if (target.prototype) return target.prototype;
  return target.prop;
}

function getTargetYield(target) {
  const prop = getTargetProp(target);
  if (!prop) return 0;
  if (target?.prototype) return Number(prop.yield || 0);
  return Number(prop.yield || 0) + Number(target?.yieldBonus || 0);
}

function getTargetPrototypeId(target) {
  if (!target) return undefined;
  if (target.prototype?._id) return target.prototype._id;
  if (target.prop?._id) return target.prop._id;
  return target.prototypeId || target.propId;
}

function evalInputNumber(runtime, nodeId, portId, fallback) {
  const node = resolveInputNodes(runtime, nodeId, portId)[0];
  if (!node) return fallback;
  const value = evalValueNode(runtime, node);
  return Number.isFinite(Number(value)) ? Number(value) : fallback;
}

function evalValueNode(runtime, node) {
  switch (node.type) {
    case "intConstant":
    case "floatConstant":
    case "rarityConstant":
    case "tagConstant":
      return Number(node.data?.value || 0);
    case "intQuantity":
      return resolveTargets(runtime, node.id, "targets").length;
    case "intKindCount":
      return new Set(resolveTargets(runtime, node.id, "targets").map(getTargetPrototypeId).filter(Boolean)).size;
    case "intRandom": {
      const min = Math.ceil(evalInputNumber(runtime, node.id, "min", 0));
      const max = Math.floor(evalInputNumber(runtime, node.id, "max", min));
      return min + Math.floor(runtime.game.rng() * (Math.max(min, max) - min + 1));
    }
    case "floatRandom": {
      const min = evalInputNumber(runtime, node.id, "min", 0);
      const max = evalInputNumber(runtime, node.id, "max", min);
      return min + runtime.game.rng() * (max - min);
    }
    case "intOperation":
      return evalIntegerOperation(
        evalInputNumber(runtime, node.id, "left", 0),
        evalInputNumber(runtime, node.id, "right", 0),
        node.data?.op || "add",
      );
    case "floatOperation":
      return evalFloatOperation(
        evalInputNumber(runtime, node.id, "left", 0),
        evalInputNumber(runtime, node.id, "right", 0),
        node.data?.op || "add",
      );
    case "roundNumber":
      return evalRoundNumber(
        evalInputNumber(runtime, node.id, "value", 0),
        node.data?.mode || "floor",
      );
    case "intAttribute":
    case "floatAttribute": {
      const target = resolveTargets(runtime, node.id, "target")[0];
      const prop = getTargetProp(target);
      if (!prop) return 0;
      if (node.data?.attribute === "rarity") return Number(prop.rarity || 0);
      if (node.data?.attribute === "tag") return Number(prop.tags?.[0] || 0);
      if (node.data?.attribute === "yield") return getTargetYield(target);
      return 0;
    }
    default:
      return 0;
  }
}

function evalIntegerOperation(left, right, op) {
  const l = Math.floor(Number.isFinite(Number(left)) ? Number(left) : 0);
  const r = Math.floor(Number.isFinite(Number(right)) ? Number(right) : 0);
  if (op === "add") return l + r;
  if (op === "subtract") return l - r;
  if (op === "multiply") return l * r;
  if (op === "divide") return r === 0 ? 0 : Math.floor(l / r);
  if (op === "mod") return r === 0 ? 0 : l % r;
  if (op === "min") return Math.min(l, r);
  if (op === "max") return Math.max(l, r);
  return 0;
}

function evalFloatOperation(left, right, op) {
  const l = Number.isFinite(Number(left)) ? Number(left) : 0;
  const r = Number.isFinite(Number(right)) ? Number(right) : 0;
  if (op === "add") return l + r;
  if (op === "subtract") return l - r;
  if (op === "multiply") return l * r;
  if (op === "divide") return r === 0 ? 0 : l / r;
  if (op === "min") return Math.min(l, r);
  if (op === "max") return Math.max(l, r);
  return 0;
}

function evalRoundNumber(value, mode) {
  const normalized = Number.isFinite(Number(value)) ? Number(value) : 0;
  if (mode === "ceil") return Math.ceil(normalized);
  if (mode === "round") return Math.round(normalized);
  return Math.floor(normalized);
}

function evalConditionInput(runtime, nodeId, portId) {
  const nodes = resolveInputNodes(runtime, nodeId, portId);
  return nodes.every((node) => evalConditionNode(runtime, node));
}

function evalConditionNode(runtime, node) {
  switch (node.type) {
    case "logicProbability":
      return runtime.game.rng() < Number(node.data?.probability || 0);
    case "logicContains": {
      const targets = resolveTargets(runtime, node.id, "targets");
      const target = resolveTargets(runtime, node.id, "target")[0];
      if (!target?.id) return false;
      return targets.some((item) => item.id === target.id);
    }
    case "logicCompareInt":
    case "logicCompareFloat": {
      const left = evalInputNumber(runtime, node.id, "left", 0);
      const right = evalInputNumber(runtime, node.id, "right", 0);
      return compare(left, right, node.data?.op || "gte");
    }
    default:
      return false;
  }
}

function compare(left, right, op) {
  if (op === "eq") return left === right;
  if (op === "ne") return left !== right;
  if (op === "gt") return left > right;
  if (op === "gte") return left >= right;
  if (op === "lt") return left < right;
  if (op === "lte") return left <= right;
  return false;
}

function executeEvent(runtime, eventType) {
  const previousEventType = runtime.eventType;
  runtime.eventType = eventType;
  try {
    for (const node of entryNodes(runtime, eventType)) {
      if (eventType === "eventTurnStart") {
        const interval = Math.max(0, Math.floor(Number(node.data?.interval || 0)));
        const loop = Math.max(0, Math.floor(Number(node.data?.loop || 0)));
        const stateKey = `${runtime.prop._id}:${runtime.actor?.id || "global"}:${node.id}`;
        const state = runtime.game.eventStates.get(stateKey) || { timer: interval, count: 0 };
        if (loop > 0 && state.count >= loop) {
          runtime.game.eventStates.set(stateKey, state);
          continue;
        }
        if (state.timer > 0) {
          state.timer -= 1;
          runtime.game.eventStates.set(stateKey, state);
          continue;
        }
        state.timer = interval;
        state.count += 1;
        runtime.game.eventStates.set(stateKey, state);
      }
      executeFlows(runtime, node.id, "flow");
    }
  } finally {
    runtime.eventType = previousEventType;
  }
}

function executeFlows(runtime, fromNodeId, portId) {
  const flows = (runtime.flowMap.get(fromNodeId) || []).filter((flow) => flow.from.portId === portId);
  for (const flow of flows) {
    const node = runtime.nodes[flow.to.nodeId];
    if (!node) continue;
    executeNode(runtime, node);
  }
}

function executeNode(runtime, node) {
  const key = `${runtime.sourceId}:${runtime.actor?.id || "none"}:${node.id}:${runtime.game.flowDepth}`;
  if (runtime.visiting.has(key) || runtime.game.flowDepth > 30) return;
  runtime.visiting.add(key);
  runtime.game.flowDepth += 1;
  try {
    if (String(node.type).startsWith("action")) executeAction(runtime, node);
    else executeFlows(runtime, node.id, "flow");
  } finally {
    runtime.game.flowDepth -= 1;
    runtime.visiting.delete(key);
  }
}

function executeAction(runtime, node) {
  if (node.data?.negative === true && runtime.actor?.purified) {
    recordPurifiedNegativeBlock(runtime.game, runtime.actor);
    executeFlows(runtime, node.id, "fail");
    return;
  }
  const conditionOk = evalConditionInput(runtime, node.id, "condition");
  if (!conditionOk) {
    executeFlows(runtime, node.id, "fail");
    return;
  }
  const previousSelectionHint = runtime.targetSelectionHint;
  if (node.type === "actionAccelerate") {
    runtime.targetSelectionHint = { preferPositiveTurnStartTimer: true };
  }
  let targets;
  try {
    targets = resolveTargets(runtime, node.id, "targets");
  } finally {
    runtime.targetSelectionHint = previousSelectionHint;
  }
  if (node.data?.negative === true) {
    targets = targets.filter((target) => {
      if (target.prototype || !target.purified) return true;
      recordPurifiedNegativeBlock(runtime.game, target);
      return false;
    });
  }
  if (node.type === "actionAccelerate") {
    targets = targets
      .slice()
      .sort((a, b) => Number(hasPositiveTurnStartTimer(runtime.game, b)) - Number(hasPositiveTurnStartTimer(runtime.game, a)));
  }
  const success = targets.length > 0 || isTargetlessAction(node.type);
  if (!success) {
    executeFlows(runtime, node.id, "fail");
    return;
  }

  if (node.type === "actionExtraYield") applyExtraYield(runtime, node, targets);
  else if (node.type === "actionGrow") applyStateDeltaAction(runtime, "growDelta", () => applyGrow(runtime, node, targets));
  else if (node.type === "actionAccelerate") applyStateDeltaAction(runtime, "accelerateDelta", () => applyAccelerate(runtime, node, targets));
  else if (node.type === "actionTransform") applyStateDeltaAction(runtime, "transformDelta", () => applyTransform(runtime, node, targets));
  else if (node.type === "actionSpawn") applyStateDeltaAction(runtime, "spawnDelta", () => applySpawn(runtime, node));
  else if (node.type === "actionEliminate") applyStateDeltaAction(runtime, "eliminateDelta", () => applyEliminate(runtime, node, targets));
  else if (node.type === "actionProtect") applyStateDeltaAction(runtime, "protectDelta", () => applyProtect(runtime, targets));
  else if (node.type === "actionPurify") applyStateDeltaAction(runtime, "purifyDelta", () => applyPurify(runtime, targets));
  else if (node.type === "actionCollect") applyStateDeltaAction(runtime, "stateDelta", () => applyCollect(runtime, node, targets));
  else if (node.type === "actionHarvest") applyHarvest(runtime);
  else if (node.type === "actionRarityWeight") applyStateDeltaAction(runtime, "rarityWeightDelta", () => applyRarityWeight(runtime, node));
  else if (node.type === "actionExtendCommission") applyStateDeltaAction(runtime, "stateDelta", () => applyExtendCommission(runtime));
  else if (node.type === "actionSwapPosition") applyStateDeltaAction(runtime, "stateDelta", () => applySwapPosition(runtime, node, targets));
  else if (node.type === "actionTriggerVisual") {
    // Pure presentation node: simulations ignore animation/effect-only actions.
  }

  executeFlows(runtime, node.id, "complete");
  targets.forEach(() => executeFlows(runtime, node.id, "each"));
}

function applyStateDeltaAction(runtime, component, fn) {
  const before = estimateGameStateValue(runtime.game);
  const result = fn();
  const after = estimateGameStateValue(runtime.game);
  const delta = after - before;
  addStateDeltaContribution(runtime.game, runtime.sourceId, delta, component);
  addIntrinsicContribution(runtime.game, runtime.prop._id, delta);
  return result;
}

function isTargetlessAction(type) {
  return type === "actionSpawn" ||
    type === "actionHarvest" ||
    type === "actionRarityWeight" ||
    type === "actionExtendCommission";
}

function applyExtraYield(runtime, node, targets) {
  const amountNode = resolveInputNodes(runtime, node.id, "amount")[0];
  const multiplierNode = resolveInputNodes(runtime, node.id, "multiplier")[0];
  const amount = amountNode ? evalValueNode(runtime, amountNode) : 0;
  const multiplier = multiplierNode ? evalValueNode(runtime, multiplierNode) : 0;
  for (const target of targets) {
    const targetYield = target.prototype ? Number(target.prototype.yield || 0) : Number(target.prop.yield || 0) + Number(target.yieldBonus || 0);
    const value = amount + multiplier * targetYield;
    runtime.game.magic += value;
    addContribution(runtime.game, runtime.sourceId, value);
    addComponentOnly(runtime.game, runtime.sourceId, value, "directMana");
    addIntrinsicContribution(runtime.game, runtime.prop._id, value);
    if (!target.prototype) addBodyContribution(runtime.game, target.prop._id, value);
  }
}

function applyGrow(runtime, node, targets) {
  const amount = evalInputNumber(runtime, node.id, "amount", 1);
  targets.forEach((target) => {
    if (target.prototype) return;
    const value = amount;
    target.yieldBonus += value;
    target.yieldBonusBySource.set(runtime.sourceId, (target.yieldBonusBySource.get(runtime.sourceId) || 0) + value);
    target.yieldBonusByIntrinsic.set(runtime.prop._id, (target.yieldBonusByIntrinsic.get(runtime.prop._id) || 0) + value);
  });
}

function applyAccelerate(runtime, node, targets) {
  const amount = evalInputNumber(runtime, node.id, "amount", -1);
  targets.forEach((target) => {
    if (target.prototype) return;
    for (const [, state] of getTurnStartEventStateEntries(runtime.game, target)) {
      state.timer = Math.max(0, state.timer + amount);
    }
  });
}

function getTurnStartEventStateEntries(game, target) {
  if (!target || target.prototype) return [];
  return Array.from(game.eventStates.entries()).filter(([key]) => {
    if (!key.includes(`:${target.id}:`)) return false;
    const nodeId = key.slice(key.lastIndexOf(":") + 1);
    return target.prop?.graph?.nodes?.[nodeId]?.type === "eventTurnStart";
  });
}

function hasPositiveTurnStartTimer(game, target) {
  return getTurnStartEventStateEntries(game, target)
    .some(([, state]) => Number(state?.timer || 0) > 0);
}

function applyTransform(runtime, node, targets) {
  const prototypes = resolveTargets(runtime, node.id, "prototypes").map((target) => target.prototype || target.prop).filter(Boolean);
  if (!prototypes.length) return;
  targets.forEach((target) => {
    if (target.prototype) return;
    const next = sample(runtime.game.rng, prototypes);
    removeRarityWeightModifiersForInstance(runtime.game, target.id);
    target.prop = next;
    target.ownerId = next?._id || target.ownerId;
    target.yieldBonus = 0;
    target.yieldBonusBySource.clear();
    target.yieldBonusByIntrinsic.clear();
  });
}

function applySpawn(runtime, node) {
  const amount = Math.max(0, Math.floor(evalInputNumber(runtime, node.id, "amount", 1)));
  const prototypes = resolveTargets(runtime, node.id, "targets").map((target) => target.prototype || target.prop).filter(Boolean);
  const emptyPositions = [];
  for (let y = 0; y < BOARD_ROWS; y += 1) {
    for (let x = 0; x < BOARD_COLUMNS; x += 1) {
      if (!runtime.game.board.some((item) => !item.eliminated && item.x === x && item.y === y)) {
        emptyPositions.push({ x, y });
      }
    }
  }
  const positions = shuffle(runtime.game.rng, emptyPositions);
  for (let index = 0; index < amount; index += 1) {
    const prop = sample(runtime.game.rng, prototypes);
    if (!prop) continue;
    const inst = createInstance(prop, prop._id);
    const pos = positions.shift();
    if (pos) {
      inst.x = pos.x;
      inst.y = pos.y;
      runtime.game.board.push(inst);
    }
    runtime.game.library.push(inst);
  }
}

function applyEliminate(runtime, node, targets) {
  const amountNode = resolveInputNodes(runtime, node.id, "amount")[0];
  const amount = amountNode
    ? Math.max(0, Math.floor(Math.abs(evalValueNode(runtime, amountNode))))
    : targets.length;
  targets.slice(0, amount || targets.length).forEach((target) => {
    if (target.prototype || target.eliminated) return;
    if (target.protected) {
      recordProtectedEliminateBlock(runtime.game, target);
      target.protected = false;
      target.protectedBy = undefined;
      target.protectedIntrinsicBy = undefined;
      return;
    }
    target.eliminated = true;
    executeEliminated(runtime.game, target);
    removeInstanceFromGame(runtime.game, target);
  });
}

function removeInstanceFromGame(game, inst) {
  game.library = game.library.filter((item) => item.id !== inst.id);
  game.board = game.board.filter((item) => item.id !== inst.id);
  game.recipes = game.recipes.filter((item) => item.id !== inst.id);
  game.supports = game.supports.filter((item) => item.id !== inst.id);
  removeRarityWeightModifiersForInstance(game, inst.id);
  for (const key of Array.from(game.eventStates.keys())) {
    if (key.includes(`:${inst.id}:`)) game.eventStates.delete(key);
  }
}

function removeRarityWeightModifiersForInstance(game, instanceId) {
  game.rarityWeightModifiers = (game.rarityWeightModifiers || []).filter((modifier) => (
    modifier.permanent || modifier.sourceInstanceId !== instanceId
  ));
}

function applyProtect(runtime, targets) {
  targets.forEach((target) => {
    if (!target.prototype) {
      target.protected = true;
      target.protectedBy = runtime.sourceId;
      target.protectedIntrinsicBy = runtime.prop._id;
    }
  });
}

function applyPurify(runtime, targets) {
  targets.forEach((target) => {
    if (!target.prototype) {
      target.purified = true;
      target.purifiedBy = runtime.sourceId;
      target.purifiedIntrinsicBy = runtime.prop._id;
    }
  });
}

function recordProtectedEliminateBlock(game, target) {
  const sourceId = target?.protectedBy;
  if (!sourceId) return;
  const value = estimateInstanceStateValue(game, target);
  addStateDeltaContribution(game, sourceId, value, "protectDelta");
  if (target.protectedIntrinsicBy) addIntrinsicContribution(game, target.protectedIntrinsicBy, value);
}

function recordPurifiedNegativeBlock(game, target) {
  const sourceId = target?.purifiedBy;
  if (!sourceId) return;
  const value = Math.max(0, estimateInstanceStateValue(game, target)) * 0.08;
  addStateDeltaContribution(game, sourceId, value, "purifyDelta");
  if (target.purifiedIntrinsicBy) addIntrinsicContribution(game, target.purifiedIntrinsicBy, value);
}

function applyCollect(runtime, node, targets) {
  if (!runtime.actor || runtime.actor.prototype) return;
  const amount = Math.max(0, evalInputNumber(runtime, node.id, "amount", 1));
  runtime.actor.collectedMagic = Number(runtime.actor.collectedMagic || 0) + amount * Math.max(0, targets.length);
}

function applyHarvest(runtime) {
  if (!runtime.actor || runtime.actor.prototype) return;
  const value = Math.max(0, Number(runtime.actor.collectedMagic || 0));
  if (!value) return;
  runtime.actor.collectedMagic = 0;
  runtime.game.magic += value;
  addContribution(runtime.game, runtime.sourceId, value);
  addComponentOnly(runtime.game, runtime.sourceId, value, "directMana");
  addIntrinsicContribution(runtime.game, runtime.prop._id, value);
  addBodyContribution(runtime.game, runtime.actor.prop._id, value);
}

function applyRarityWeight(runtime, node) {
  const rarityValues = resolveInputNodes(runtime, node.id, "rarity")
    .map((source) => evalValueNode(runtime, source))
    .filter((value) => Number.isFinite(Number(value)))
    .map(Number);
  if (!rarityValues.length) return;
  const multiplier = Math.max(0, evalInputNumber(runtime, node.id, "multiplier", 1));
  const permanent = Number(runtime.actor?.prop?.type) === 2 && runtime.eventType === "eventUse";
  rarityValues.forEach((rarity) => {
    const key = `${runtime.actor?.id || runtime.sourceId}:${node.id}:${rarity}`;
    if ((runtime.game.rarityWeightModifiers || []).some((modifier) => modifier.key === key)) return;
    runtime.game.rarityWeightModifiers.push({
      key,
      rarity,
      multiplier,
      sourceId: runtime.sourceId,
      sourceInstanceId: runtime.actor?.id,
      permanent,
    });
  });
}

function applyExtendCommission(runtime) {
  const commissionIndex = Number(runtime.game.currentCommissionIndex || 0);
  if (runtime.game.extendedCommissions.has(commissionIndex)) return;
  runtime.game.extendedCommissions.add(commissionIndex);
  runtime.game.commissionExtraTurns[commissionIndex] = Math.min(
    MAX_COMMISSION_EXTRA_TURNS,
    (runtime.game.commissionExtraTurns[commissionIndex] || 0) + 1,
  );
}

function applySwapPosition(runtime, node, targets) {
  const positions = resolveTargets(runtime, node.id, "positions")
    .filter((target) => !target.prototype && !target.eliminated && target.x >= 0 && target.y >= 0);
  const movableTargets = targets.filter((target) => !target.prototype && !target.eliminated && target.x >= 0 && target.y >= 0);
  movableTargets.forEach((target, index) => {
    const destination = positions[index % Math.max(1, positions.length)];
    if (!destination || destination.id === target.id) return;
    const currentX = target.x;
    const currentY = target.y;
    target.x = destination.x;
    target.y = destination.y;
    destination.x = currentX;
    destination.y = currentY;
  });
}

function executeEliminated(game, inst) {
  const runtime = makeRuntime(inst.prop, inst, game, inst.ownerId || inst.prop._id);
  executeEvent(runtime, "eventEliminated");
}

function drawBoard(rng, library) {
  const positions = shuffle(rng, Array.from({ length: BOARD_SIZE }, (_, index) => ({
    x: index % BOARD_COLUMNS,
    y: Math.floor(index / BOARD_COLUMNS),
  })));
  return shuffle(rng, library).slice(0, BOARD_SIZE).map((inst, index) => {
    inst.eliminated = false;
    inst.protected = false;
    inst.purified = false;
    inst.protectedBy = undefined;
    inst.protectedIntrinsicBy = undefined;
    inst.purifiedBy = undefined;
    inst.purifiedIntrinsicBy = undefined;
    inst.x = positions[index].x;
    inst.y = positions[index].y;
    return inst;
  });
}

function resetRoundTransientState(game) {
  [game.library, game.recipes, game.supports].forEach((instances) => {
    instances.forEach((inst) => {
      inst.protected = false;
      inst.purified = false;
      inst.protectedBy = undefined;
      inst.protectedIntrinsicBy = undefined;
      inst.purifiedBy = undefined;
      inst.purifiedIntrinsicBy = undefined;
    });
  });
}

function getHeldInstances(game) {
  const map = new Map();
  [game.library, game.recipes, game.supports, game.board].forEach((instances) => {
    instances.forEach((inst) => {
      if (!inst.eliminated) map.set(inst.id, inst);
    });
  });
  return Array.from(map.values());
}

function executeHeldEvents(game) {
  for (const inst of getHeldInstances(game)) {
    executeEvent(makeRuntime(inst.prop, inst, game, inst.ownerId || inst.prop._id), "eventAcquire");
  }
}

function updatePresenceStats(game) {
  game.board
    .filter((inst) => !inst.eliminated)
    .forEach((inst) => addPresentRound(game, inst.prop._id));
  game.recipes.forEach((inst) => addPresentRound(game, inst.prop._id));
  game.supports.forEach((inst) => addPresentRound(game, inst.prop._id));
}

function simulateGame(rng, gameIndex, profile, data, stats, scopeStats, profiler) {
  let systems;
  let pools;
  measurePerformance(profiler, "setup", () => {
    systems = chooseSystemsForGame(rng, data);
    pools = buildPools(data.props, systems);
  });
  const commissionMagic = new Array(COMMISSION_TURNS.length).fill(0);
  const allScope = ensureScopeStats(
    scopeStats,
    data,
    "all",
    "all",
    "全部数据",
  );
  const profileScope = ensureScopeStats(
    scopeStats,
    data,
    `profile:${profile.key}`,
    "profile",
    `付费：${profile.name}`,
    { profileKey: profile.key },
  );
  const systemScopeKeys = Array.from(systems).map((tag) => {
    const label = data.tagMap.get(Number(tag)) || String(tag);
    const scope = ensureScopeStats(
      scopeStats,
      data,
      `system:${tag}`,
      "system",
      `体系：${label}`,
      { systemTag: Number(tag) },
    );
    scope.games += 1;
    scope.turns += COMMISSION_TURNS.reduce((sum, value) => sum + value, 0);
    return scope.key;
  });
  const gameTurnCount = COMMISSION_TURNS.reduce((sum, value) => sum + value, 0);
  allScope.games += 1;
  allScope.turns += gameTurnCount;
  profileScope.games += 1;
  profileScope.turns += gameTurnCount;
  const baseScopeKeys = ["all", `profile:${profile.key}`, ...systemScopeKeys];
  const game = {
    rng,
    systems,
    pools,
    data,
    indexes: data.indexes,
    stats,
    library: [],
    board: [],
    recipes: [],
    supports: [],
    supportPickCount: 0,
    magic: 0,
    eventStates: new Map(),
    rarityWeightModifiers: [],
    commissionExtraTurns: new Array(COMMISSION_TURNS.length).fill(0),
    extendedCommissions: new Set(),
    commissionBlockedPropIds: new Set(),
    currentCommissionIndex: 0,
    globalTurn: 0,
    flowDepth: 0,
    scopeStats,
    currentScopeKeys: baseScopeKeys,
  };

  const commonMaterials = pools.materials.filter((prop) => prop.rarity === 1);
  measurePerformance(profiler, "initialLibrary", () => {
    for (let index = 0; index < INITIAL_MATERIAL_COUNT; index += 1) {
      const prop = pickInitialMaterial(rng, commonMaterials.length ? commonMaterials : pools.materials);
      if (prop) addLibraryInstance(game, prop);
    }
  });

  let globalTurn = 0;
  for (let commission = 0; commission < COMMISSION_TURNS.length; commission += 1) {
    game.currentCommissionIndex = commission;
    game.commissionBlockedPropIds = new Set();
    const beforeMagic = game.magic;
    const commissionScope = ensureScopeStats(
      scopeStats,
      data,
      `commission:${commission + 1}`,
      "commission",
      `第${commission + 1}次委托`,
      { commissionIndex: commission + 1 },
    );
    commissionScope.games += 1;
    commissionScope.turns += COMMISSION_TURNS[commission];
    let localTurn = 0;
    while (localTurn < getCommissionTurnLimit(game, commission)) {
      const commissionTurnLimit = getCommissionTurnLimit(game, commission);
      localTurn += 1;
      globalTurn += 1;
      game.globalTurn = globalTurn;
      const roundScope = ensureScopeStats(
        scopeStats,
        data,
        `round:${globalTurn}`,
        "round",
        `第${globalTurn}轮`,
        {
          commissionIndex: commission + 1,
          roundIndex: globalTurn,
        },
      );
      roundScope.games += 1;
      roundScope.turns += 1;
      const profileCommissionScope = ensureScopeStats(
        scopeStats,
        data,
        `filter:profile:${profile.key}:commission:${commission + 1}`,
        "filter",
        `${profile.name} / 第${commission + 1}次委托`,
        {
          profileKey: profile.key,
          commissionIndex: commission + 1,
        },
      );
      const profileRoundScope = ensureScopeStats(
        scopeStats,
        data,
        `filter:profile:${profile.key}:round:${globalTurn}`,
        "filter",
        `${profile.name} / 第${globalTurn}轮`,
        {
          profileKey: profile.key,
          commissionIndex: commission + 1,
          roundIndex: globalTurn,
        },
      );
      touchScope(profileCommissionScope, gameIndex, 1);
      touchScope(profileRoundScope, gameIndex, 1);
      game.currentScopeKeys = baseScopeKeys.concat(
        commissionScope.key,
        roundScope.key,
        profileCommissionScope.key,
        profileRoundScope.key,
      );
      measurePerformance(profiler, "drawBoard", () => {
        resetRoundTransientState(game);
        game.board = drawBoard(rng, game.library);
      });
      measurePerformance(profiler, "presenceStats", () => {
        updatePresenceStats(game);
      });
      measurePerformance(profiler, "heldEvents", () => {
        executeHeldEvents(game);
      });

      measurePerformance(profiler, "supportEvents", () => {
        for (const support of game.supports.slice()) {
          executeEvent(makeRuntime(support.prop, support, game, support.ownerId || support.prop._id), "eventTurnStart");
        }
      });
      const currentLocalTurn = localTurn;
      const currentCommissionTurnLimit = commissionTurnLimit;
      measurePerformance(profiler, "supportUse", () => {
        const supportsToUse = game.supports.filter((support) => shouldUseSupportThisTurn(support, currentLocalTurn, currentCommissionTurnLimit));
        for (const support of supportsToUse) {
          if (support.used) continue;
          support.used = true;
          addUse(game, support.prop._id);
          executeEvent(makeRuntime(support.prop, support, game, support.ownerId || support.prop._id), "eventUse");
          removeInstanceFromGame(game, support);
        }
      });
      measurePerformance(profiler, "recipeEvents", () => {
        for (const recipe of game.recipes.slice()) {
          executeEvent(makeRuntime(recipe.prop, recipe, game, recipe.ownerId || recipe.prop._id), "eventTurnStart");
        }
      });
      measurePerformance(profiler, "boardEvents", () => {
        for (const inst of game.board.slice()) {
          executeEvent(makeRuntime(inst.prop, inst, game, inst.ownerId || inst.prop._id), "eventTurnStart");
        }
      });

      measurePerformance(profiler, "baseYield", () => {
        game.board.filter((inst) => !inst.eliminated).forEach((inst) => creditBaseYield(game, inst));
      });

      const materialPool = pools.materials;
      measurePerformance(profiler, "materialChoice", () => {
        const material = pickOffer(rng, materialPool, commission, data, profile, 1, new Set(), game);
        if (material) addLibraryInstance(game, material);
      });
      measurePerformance(profiler, "pruneLibrary", () => {
        const prunedMaterialCount = pruneMaterialLibrary(game, profile, data);
        if (prunedMaterialCount) addScopeMetric(game, "prunedMaterialCount", prunedMaterialCount);
      });

      const supportPool = pools.supports;
      measurePerformance(profiler, "supportChoice", () => {
        if (profile.key !== "free" && game.supportPickCount < MAX_SUPPORT_COUNT) {
          const support = pickOffer(rng, supportPool, commission, data, profile, 1, game.commissionBlockedPropIds, game);
          if (support) {
            game.supportPickCount += 1;
            addSupportInstance(game, support);
            if (isLongHandledSpoon(support)) game.commissionBlockedPropIds.add(support._id);
          }
        }
      });
      measurePerformance(profiler, "libraryStats", () => {
        recordLibrarySize(game);
      });
    }

    const recipePool = pools.recipes;
    measurePerformance(profiler, "recipeChoice", () => {
      const ownedRecipeIds = new Set(game.recipes.map((recipe) => recipe.prop._id));
      const recipe = pickOffer(rng, recipePool, commission, data, profile, 1, ownedRecipeIds, game);
      if (recipe && game.recipes.length < MAX_RECIPE_COUNT) {
        addRecipeInstance(game, recipe);
      }
    });
    commissionMagic[commission] = game.magic - beforeMagic;
  }

  return {
    magic: game.magic,
    commissionMagic,
  };
}

function pickInitialMaterial(rng, pool) {
  if (!pool.length) return undefined;
  return sample(rng, pool);
}

function shouldUseSupportThisTurn(support, localTurn, commissionTurnLimit) {
  if (!support?.prop) return false;
  const isCollectSupport = propGraphHasNodeType(support.prop, "actionCollect") ||
    propGraphHasNodeType(support.prop, "actionHarvest");
  if (!isCollectSupport) return true;
  if (!propGraphHasNodeType(support.prop, "actionHarvest")) return false;
  return Number(support.collectedMagic || 0) > 0 && localTurn >= commissionTurnLimit;
}

function propGraphHasNodeType(prop, type) {
  if (prop?.compiledGraph?.nodeTypeSet) return prop.compiledGraph.nodeTypeSet.has(type);
  return Object.values(prop?.graph?.nodes || {}).some((node) => node?.type === type);
}

function isLongHandledSpoon(prop) {
  return String(prop?.name || "").includes("长柄勺");
}

function getCommissionTurnLimit(game, commissionIndex) {
  const baseTurns = COMMISSION_TURNS[commissionIndex] || 0;
  const extraTurns = Math.min(
    MAX_COMMISSION_EXTRA_TURNS,
    Math.max(0, Math.floor(Number(game.commissionExtraTurns?.[commissionIndex] || 0))),
  );
  return baseTurns + extraTurns;
}

function buildSimulationResult(config, data, stats, profileStats, totalMagic, commissionTotals, scopeStats, elapsedMs, profiler) {
  const totalTurns = config.games * COMMISSION_TURNS.reduce((sum, value) => sum + value, 0);
  const commissionAverageMagic = (commissionTotals || []).map((value) => round(value / Math.max(1, config.games), 4));
  const rows = buildStatRows(stats, data, totalTurns);
  const statScopes = Array.from(scopeStats.values())
    .map((scope) => {
      const scopeRows = buildStatRows(scope.stats, data, Math.max(1, scope.turns));
      return {
        key: scope.key,
        type: scope.type,
        label: scope.label,
        meta: scope.meta,
        rows: scopeRows,
        summary: buildSummaryFromRows(
          scopeRows,
          Math.max(1, scope.games),
          Math.max(1, scope.turns),
          [],
          0,
          scope.metrics,
        ),
      };
    })
    .sort((a, b) => compareScopeOrder(a, b));

  const profileSummaries = Array.from(profileStats.values()).map((profile) => {
    const profileTurns = profile.games * COMMISSION_TURNS.reduce((sum, value) => sum + value, 0);
    return {
      key: profile.key,
      name: profile.name,
      games: profile.games,
      totalMagic: round(profile.totalMagic, 4),
      averageMagicPerGame: round(profile.totalMagic / Math.max(1, profile.games), 4),
      averageMagicPerTurn: round(profile.totalMagic / Math.max(1, profileTurns), 4),
    };
  });

  return {
    config,
    strategyText: getPropSimulationStrategyText(config),
    summary: buildSummaryFromRows(
      rows,
      config.games,
      totalTurns,
      commissionAverageMagic,
      elapsedMs,
      scopeStats.get("all")?.metrics,
      buildPerformanceSummary(profiler),
    ),
    profileSummaries,
    typeSummaries: buildTypeSummaries(rows),
    rows,
    statScopes,
  };
}

function buildSummaryFromRows(rows, games, totalTurns, commissionAverageMagic, elapsedMs, metrics = {}, performance = []) {
  const totalMagic = rows.reduce((sum, row) => sum + Number(row.contribution || 0), 0);
  const getFinalValue = (row) => Number(row.finalValue ?? row.intrinsicPresentStrength ?? row.sourcePresentStrength ?? row.presentStrength ?? 0);
  return {
    games,
    totalTurns,
    totalMagic: round(totalMagic, 4),
    averageMagicPerGame: round(totalMagic / Math.max(1, games), 4),
    averageMagicPerTurn: round(totalMagic / Math.max(1, totalTurns), 4),
    averageLibrarySize: round((metrics.librarySizeSum || 0) / Math.max(1, metrics.librarySizeSamples || 0), 4),
    maxLibrarySize: metrics.maxLibrarySize || 0,
    prunedMaterialCount: metrics.prunedMaterialCount || 0,
    commissionAverageMagic,
    propCount: rows.length,
    positivePropCount: rows.filter((row) => getFinalValue(row) > 0).length,
    negativePropCount: rows.filter((row) => getFinalValue(row) < 0).length,
    presentPropCount: rows.filter((row) => row.presentRounds > 0).length,
    neverPresentPropCount: rows.filter((row) => row.presentRounds <= 0).length,
    elapsedMs,
    performance,
  };
}

function buildStatRows(stats, data, totalTurns) {
  return Array.from(stats.values()).map((item) => {
    const sourceGlobalStrength = item.contribution / Math.max(1, totalTurns);
    const sourcePresentStrength = item.presentRounds ? item.contribution / item.presentRounds : 0;
    const intrinsicGlobalStrength = item.intrinsicContribution / Math.max(1, totalTurns);
    const intrinsicPresentStrength = item.presentRounds ? item.intrinsicContribution / item.presentRounds : 0;
    const bodyGlobalStrength = item.bodyContribution / Math.max(1, totalTurns);
    const bodyPresentStrength = item.presentRounds ? item.bodyContribution / item.presentRounds : 0;
    const valueEstimate = estimatePropValueFromSimulationStat(
      item,
      data,
      totalTurns,
      sourceGlobalStrength,
      sourcePresentStrength,
      intrinsicGlobalStrength,
      intrinsicPresentStrength,
    );
    return {
      id: item.prop._id,
      name: item.prop.name,
      assetId: item.prop.assetId,
      type: item.prop.type,
      typeName: data.typeMap.get(item.prop.type) || String(item.prop.type),
      rarity: item.prop.rarity,
      rarityName: data.rarityMap.get(item.prop.rarity) || String(item.prop.rarity),
      tags: item.prop.tags,
      tagNames: item.prop.tags.map((tag) => data.tagMap.get(tag) || String(tag)),
      derived: item.prop.derived === true,
      contribution: round(item.contribution, 4),
      globalStrength: round(sourceGlobalStrength, 4),
      presentStrength: round(sourcePresentStrength, 4),
      sourceContribution: round(item.contribution, 4),
      sourceGlobalStrength: round(sourceGlobalStrength, 4),
      sourcePresentStrength: round(sourcePresentStrength, 4),
      intrinsicContribution: round(item.intrinsicContribution, 4),
      intrinsicGlobalStrength: round(intrinsicGlobalStrength, 4),
      intrinsicPresentStrength: round(intrinsicPresentStrength, 4),
      bodyContribution: round(item.bodyContribution, 4),
      bodyGlobalStrength: round(bodyGlobalStrength, 4),
      bodyPresentStrength: round(bodyPresentStrength, 4),
      ...valueEstimate,
      presentRounds: item.presentRounds,
      uses: item.uses,
      effectEvents: item.effectEvents || 0,
    };
  }).sort((a, b) => Number(b.finalValue ?? b.intrinsicPresentStrength ?? b.sourcePresentStrength ?? b.presentStrength ?? 0) - Number(a.finalValue ?? a.intrinsicPresentStrength ?? a.sourcePresentStrength ?? a.presentStrength ?? 0));
}

function estimatePropValueFromSimulationStat(
  item,
  data,
  totalTurns,
  sourceGlobalStrength,
  sourcePresentStrength,
  intrinsicGlobalStrength,
  intrinsicPresentStrength,
) {
  const staticResult = getStaticStrengthResult(item.prop, data);
  const staticPrior = staticResult.value;
  const expectedValue = staticResult.expected;
  const intrinsicValue = item.presentRounds ? intrinsicPresentStrength : intrinsicGlobalStrength;
  const sourceValue = item.presentRounds ? sourcePresentStrength : sourceGlobalStrength;
  const triggerValue = pickObservedCausalValue(intrinsicValue, sourceValue);
  const poolValue = 0;
  const synergyValue = 0;
  const balanceValue = triggerValue + poolValue;
  const sampleCount = Math.max(
    0,
    Number(item.presentRounds || 0) +
      Number(item.uses || 0) +
      Number(item.effectEvents || 0),
  );
  const effectiveSampleCount = Math.max(
    0,
    Number(item.presentRounds || 0) +
      Number(item.uses || 0) * 10 +
      Number(item.effectEvents || 0) * EFFECT_EVENT_SAMPLE_WEIGHT,
  );
  const confidence = getValueConfidence(item.prop, effectiveSampleCount);
  const finalValue = confidence * balanceValue + (1 - confidence) * staticPrior;
  const calibration = createStrengthCalibrationSnapshot(staticPrior, finalValue, item.prop);
  const confidenceRadius = estimateValueConfidenceRadius(
    finalValue,
    balanceValue,
    staticPrior,
    effectiveSampleCount,
    expectedValue,
  );
  const deviation = finalValue - expectedValue;
  const components = buildValueComponents(item, totalTurns, finalValue, poolValue);
  return {
    finalValue: round(finalValue, 4),
    balanceValue: round(balanceValue, 4),
    ...calibration,
    triggerValue: round(triggerValue, 4),
    poolValue: round(poolValue, 4),
    synergyValue: round(synergyValue, 4),
    staticPrior: round(staticPrior, 4),
    expectedValue: round(expectedValue, 4),
    deviation: round(deviation, 4),
    deviationRate: round(expectedValue ? deviation / expectedValue * 100 : 0, 4),
    confidenceLow: round(finalValue - confidenceRadius, 4),
    confidenceHigh: round(finalValue + confidenceRadius, 4),
    confidenceRadius: round(confidenceRadius, 4),
    sampleCount,
    effectiveSampleCount: round(effectiveSampleCount, 4),
    components,
  };
}

function pickObservedCausalValue(intrinsicValue, sourceValue) {
  const intrinsic = Number.isFinite(intrinsicValue) ? intrinsicValue : 0;
  const source = Number.isFinite(sourceValue) ? sourceValue : 0;
  return Math.abs(source) > Math.abs(intrinsic) ? source : intrinsic;
}

function createStrengthCalibrationSnapshot(actualCalculatedStrength, balancedSimulationStrength, prop) {
  const actual = Number.isFinite(actualCalculatedStrength) ? Number(actualCalculatedStrength) : 0;
  const balanced = Number.isFinite(balancedSimulationStrength) ? Number(balancedSimulationStrength) : 0;
  const coefficient = actual > STRENGTH_COEFFICIENT_ZERO_EPSILON
    ? balanced / actual
    : null;
  return {
    actualCalculatedStrength: round(actual, 4),
    balancedSimulationStrength: round(balanced, 4),
    strengthCoefficient: coefficient === null ? null : round(coefficient, 6),
    propUpdatedAt: Number(prop?.updatedAt || 0),
  };
}

function getStaticStrengthResult(prop, data) {
  if (!prop || !data) return { value: 0, expected: 0 };
  const id = String(prop._id);
  const expected = expectedStrength(prop, data);
  if (data.staticValueCache?.has(id)) {
    return {
      value: data.staticValueCache.get(id),
      expected,
    };
  }
  try {
    const result = getPropGraphStrength(prop, { props: data.props, strengths: data.strengths });
    data.staticValueCache?.set(id, result.value);
    return result;
  } catch (_) {
    data.staticValueCache?.set(id, 0);
    return { value: 0, expected };
  }
}

function getValueConfidence(prop, effectiveSampleCount) {
  const prior = VALUE_CONFIDENCE_PRIOR_BY_RARITY[Number(prop?.rarity)] || 400;
  return clamp(effectiveSampleCount / Math.max(1, effectiveSampleCount + prior), 0, 0.95);
}

function estimateValueConfidenceRadius(finalValue, balanceValue, staticPrior, effectiveSampleCount, expectedValue) {
  const samplingRadius = Math.abs(finalValue) / Math.sqrt(Math.max(1, effectiveSampleCount));
  const modelRadius = Math.abs(balanceValue - staticPrior) * (1 - Math.min(0.95, effectiveSampleCount / Math.max(1, effectiveSampleCount + 300)));
  const floorRadius = Math.max(0.25, Math.abs(expectedValue || finalValue || 1) * 0.05);
  return Math.max(floorRadius, samplingRadius * 1.96, modelRadius);
}

function buildValueComponents(item, totalTurns, finalValue, poolValue) {
  const divisor = Math.max(1, item.presentRounds || totalTurns);
  const components = createEmptyValueComponents();
  for (const key of VALUE_COMPONENT_KEYS) {
    components[key] = Number(item.components?.[key] || 0) / divisor;
  }
  components.pool += poolValue;
  components.synergy += Math.max(0, poolValue);
  const assigned = VALUE_COMPONENT_KEYS
    .filter((key) => !MIRROR_VALUE_COMPONENT_KEYS.has(key))
    .reduce((sum, key) => sum + Number(components[key] || 0), 0);
  const residual = finalValue - assigned;
  allocateResidualValueToComponents(components, item.prop, residual);
  return Object.fromEntries(VALUE_COMPONENT_KEYS.map((key) => [key, round(components[key], 4)]));
}

function allocateResidualValueToComponents(components, prop, residual) {
  if (Math.abs(residual) < 0.0001) return;
  if (residual < 0) {
    components.cost += residual;
    return;
  }
  const nodes = Object.values(prop?.graph?.nodes || {});
  const buckets = [];
  if (nodes.some((node) => node?.type === "actionSpawn")) buckets.push("spawn");
  if (nodes.some((node) => node?.type === "actionTransform")) buckets.push("transform");
  if (nodes.some((node) => node?.type === "actionEliminate")) buckets.push("eliminate");
  if (nodes.some((node) => node?.type === "actionProtect" || node?.type === "actionPurify")) buckets.push("defense");
  if (nodes.some((node) => node?.type === "actionRarityWeight")) buckets.push("weight");
  if (nodes.some((node) => String(node?.type || "").startsWith("logic") || node?.type === "targetFilter")) buckets.push("condition");
  const targets = buckets.length ? buckets : ["direct"];
  targets.forEach((key) => {
    components[key] += residual / targets.length;
  });
}

function compareScopeOrder(a, b) {
  const typeRank = { commission: 1, round: 2, system: 3, profile: 4, filter: 5 };
  const typeDiff = (typeRank[a.type] || 99) - (typeRank[b.type] || 99);
  if (typeDiff) return typeDiff;
  return String(a.key).localeCompare(String(b.key), "zh-Hans-CN", { numeric: true });
}

function buildTypeSummaries(rows) {
  const map = new Map();
  rows.forEach((row) => {
    const key = `${row.type}:${row.rarity}`;
    if (!map.has(key)) {
      map.set(key, {
        type: row.type,
        typeName: row.typeName,
        rarity: row.rarity,
        rarityName: row.rarityName,
        count: 0,
        globalStrengthSum: 0,
        presentStrengthSum: 0,
      });
    }
    const item = map.get(key);
    item.count += 1;
    item.globalStrengthSum += Number(row.finalValue ?? row.intrinsicPresentStrength ?? row.sourcePresentStrength ?? row.presentStrength ?? 0);
    item.presentStrengthSum += Number(row.balanceValue ?? row.intrinsicPresentStrength ?? row.sourcePresentStrength ?? row.presentStrength ?? 0);
  });
  return Array.from(map.values())
    .map((item) => ({
      type: item.type,
      typeName: item.typeName,
      rarity: item.rarity,
      rarityName: item.rarityName,
      count: item.count,
      averageGlobalStrength: round(item.globalStrengthSum / Math.max(1, item.count), 4),
      averagePresentStrength: round(item.presentStrengthSum / Math.max(1, item.count), 4),
    }))
    .sort((a, b) => a.type - b.type || a.rarity - b.rarity);
}

function mergeStatRows(rowLists, totalTurns) {
  const map = new Map();
  rowLists.flat().forEach((row) => {
    if (!row?.id) return;
    if (!map.has(row.id)) {
      map.set(row.id, {
        ...row,
        contribution: 0,
        globalStrength: 0,
        presentStrength: 0,
        sourceContribution: 0,
        sourceGlobalStrength: 0,
        sourcePresentStrength: 0,
        intrinsicContribution: 0,
        intrinsicGlobalStrength: 0,
        intrinsicPresentStrength: 0,
        bodyContribution: 0,
        bodyGlobalStrength: 0,
        bodyPresentStrength: 0,
        finalValue: 0,
        balanceValue: 0,
        actualCalculatedStrength: 0,
        balancedSimulationStrength: 0,
        strengthCoefficient: null,
        propUpdatedAt: Number(row.propUpdatedAt || 0),
        triggerValue: 0,
        poolValue: 0,
        synergyValue: 0,
        staticPrior: 0,
        expectedValue: 0,
        deviation: 0,
        deviationRate: 0,
        confidenceLow: 0,
        confidenceHigh: 0,
        confidenceRadius: 0,
        sampleCount: 0,
        effectiveSampleCount: 0,
        valueWeight: 0,
        componentSums: createEmptyValueComponents(),
        presentRounds: 0,
        uses: 0,
        effectEvents: 0,
      });
    }
    const item = map.get(row.id);
    const valueWeight = Math.max(1, Number(row.effectiveSampleCount || row.presentRounds || row.uses || 1));
    item.contribution += Number(row.sourceContribution ?? row.contribution ?? 0);
    item.sourceContribution += Number(row.sourceContribution ?? row.contribution ?? 0);
    item.intrinsicContribution += Number(row.intrinsicContribution ?? row.sourceContribution ?? row.contribution ?? 0);
    item.bodyContribution += Number(row.bodyContribution ?? row.contribution ?? 0);
    item.presentRounds += Number(row.presentRounds || 0);
    item.uses += Number(row.uses || 0);
    item.effectEvents += Number(row.effectEvents || 0);
    item.finalValue += Number(row.finalValue ?? row.intrinsicPresentStrength ?? row.sourcePresentStrength ?? row.presentStrength ?? 0) * valueWeight;
    item.balanceValue += Number(row.balanceValue ?? row.intrinsicPresentStrength ?? row.sourcePresentStrength ?? row.presentStrength ?? 0) * valueWeight;
    item.actualCalculatedStrength += Number(row.actualCalculatedStrength ?? row.staticPrior ?? 0) * valueWeight;
    item.balancedSimulationStrength += Number(row.balancedSimulationStrength ?? row.finalValue ?? row.intrinsicPresentStrength ?? row.sourcePresentStrength ?? row.presentStrength ?? 0) * valueWeight;
    item.propUpdatedAt = Math.max(Number(item.propUpdatedAt || 0), Number(row.propUpdatedAt || 0));
    item.triggerValue += Number(row.triggerValue ?? row.intrinsicPresentStrength ?? row.sourcePresentStrength ?? row.presentStrength ?? 0) * valueWeight;
    item.poolValue += Number(row.poolValue || 0) * valueWeight;
    item.synergyValue += Number(row.synergyValue || 0) * valueWeight;
    item.staticPrior += Number(row.staticPrior || 0) * valueWeight;
    item.expectedValue += Number(row.expectedValue || 0) * valueWeight;
    item.confidenceLow += Number(row.confidenceLow ?? row.finalValue ?? 0) * valueWeight;
    item.confidenceHigh += Number(row.confidenceHigh ?? row.finalValue ?? 0) * valueWeight;
    item.confidenceRadius += Number(row.confidenceRadius || 0) * valueWeight;
    item.sampleCount += Number(row.sampleCount || 0);
    item.effectiveSampleCount += Number(row.effectiveSampleCount || 0);
    item.valueWeight += valueWeight;
    VALUE_COMPONENT_KEYS.forEach((key) => {
      item.componentSums[key] += Number(row.components?.[key] || 0) * valueWeight;
    });
  });
  return Array.from(map.values()).map((row) => {
    const sourceGlobalStrength = row.sourceContribution / Math.max(1, totalTurns);
    const sourcePresentStrength = row.presentRounds ? row.sourceContribution / row.presentRounds : 0;
    const intrinsicGlobalStrength = row.intrinsicContribution / Math.max(1, totalTurns);
    const intrinsicPresentStrength = row.presentRounds ? row.intrinsicContribution / row.presentRounds : 0;
    const bodyGlobalStrength = row.bodyContribution / Math.max(1, totalTurns);
    const bodyPresentStrength = row.presentRounds ? row.bodyContribution / row.presentRounds : 0;
    const valueWeight = Math.max(1, row.valueWeight || 1);
    const finalValue = row.finalValue / valueWeight;
    const actualCalculatedStrength = row.actualCalculatedStrength / valueWeight;
    const expectedValue = row.expectedValue / valueWeight;
    const deviation = finalValue - expectedValue;
    const calibration = createStrengthCalibrationSnapshot(
      actualCalculatedStrength,
      finalValue,
      { updatedAt: row.propUpdatedAt },
    );
    return {
      ...row,
      contribution: round(row.sourceContribution, 4),
      globalStrength: round(sourceGlobalStrength, 4),
      presentStrength: round(sourcePresentStrength, 4),
      sourceContribution: round(row.sourceContribution, 4),
      sourceGlobalStrength: round(sourceGlobalStrength, 4),
      sourcePresentStrength: round(sourcePresentStrength, 4),
      intrinsicContribution: round(row.intrinsicContribution, 4),
      intrinsicGlobalStrength: round(intrinsicGlobalStrength, 4),
      intrinsicPresentStrength: round(intrinsicPresentStrength, 4),
      bodyContribution: round(row.bodyContribution, 4),
      bodyGlobalStrength: round(bodyGlobalStrength, 4),
      bodyPresentStrength: round(bodyPresentStrength, 4),
      finalValue: round(finalValue, 4),
      balanceValue: round(row.balanceValue / valueWeight, 4),
      ...calibration,
      triggerValue: round(row.triggerValue / valueWeight, 4),
      poolValue: round(row.poolValue / valueWeight, 4),
      synergyValue: round(row.synergyValue / valueWeight, 4),
      staticPrior: round(row.staticPrior / valueWeight, 4),
      expectedValue: round(expectedValue, 4),
      deviation: round(deviation, 4),
      deviationRate: round(expectedValue ? deviation / expectedValue * 100 : 0, 4),
      confidenceLow: round(row.confidenceLow / valueWeight, 4),
      confidenceHigh: round(row.confidenceHigh / valueWeight, 4),
      confidenceRadius: round(row.confidenceRadius / valueWeight, 4),
      sampleCount: row.sampleCount,
      effectiveSampleCount: round(row.effectiveSampleCount, 4),
      components: Object.fromEntries(VALUE_COMPONENT_KEYS.map((key) => [
        key,
        round(row.componentSums[key] / valueWeight, 4),
      ])),
      presentRounds: row.presentRounds,
      uses: row.uses,
      effectEvents: row.effectEvents,
    };
  }).sort((a, b) => Number(b.finalValue ?? b.intrinsicPresentStrength ?? b.sourcePresentStrength ?? b.presentStrength ?? 0) - Number(a.finalValue ?? a.intrinsicPresentStrength ?? a.sourcePresentStrength ?? a.presentStrength ?? 0));
}

function mergeProfileSummaries(results) {
  const map = new Map();
  results.flatMap((result) => result.profileSummaries || []).forEach((profile) => {
    if (!map.has(profile.key)) {
      map.set(profile.key, {
        key: profile.key,
        name: profile.name,
        games: 0,
        totalMagic: 0,
      });
    }
    const item = map.get(profile.key);
    item.games += Number(profile.games || 0);
    item.totalMagic += Number(profile.totalMagic || 0);
  });
  const turnsPerGame = COMMISSION_TURNS.reduce((sum, value) => sum + value, 0);
  return Array.from(map.values()).map((profile) => ({
    ...profile,
    totalMagic: round(profile.totalMagic, 4),
    averageMagicPerGame: round(profile.totalMagic / Math.max(1, profile.games), 4),
    averageMagicPerTurn: round(profile.totalMagic / Math.max(1, profile.games * turnsPerGame), 4),
  }));
}

function mergeStatScopes(results) {
  const map = new Map();
  results.flatMap((result) => result.statScopes || []).forEach((scope) => {
    if (!map.has(scope.key)) {
      map.set(scope.key, {
        key: scope.key,
        type: scope.type,
        label: scope.label,
        meta: scope.meta,
        summaries: [],
        rowLists: [],
      });
    }
    const item = map.get(scope.key);
    item.summaries.push(scope.summary);
    item.rowLists.push(scope.rows || []);
  });
  return Array.from(map.values()).map((scope) => {
    const games = scope.summaries.reduce((sum, summary) => sum + Number(summary?.games || 0), 0);
    const totalTurns = scope.summaries.reduce((sum, summary) => sum + Number(summary?.totalTurns || 0), 0);
    const rows = mergeStatRows(scope.rowLists, totalTurns);
    return {
      key: scope.key,
      type: scope.type,
      label: scope.label,
      meta: scope.meta,
      rows,
      summary: buildSummaryFromRows(
        rows,
        games,
        totalTurns,
        mergeCommissionAverageMagic(scope.summaries.map((summary) => ({ summary }))),
        0,
        mergeSummaryMetrics(scope.summaries),
      ),
    };
  }).sort((a, b) => compareScopeOrder(a, b));
}

function mergeCommissionAverageMagic(results) {
  const summaries = results.map((result) => result.summary).filter(Boolean);
  const totalGames = summaries.reduce((sum, summary) => sum + Number(summary.games || 0), 0);
  return COMMISSION_TURNS.map((_, index) => {
    const total = summaries.reduce((sum, summary) =>
      sum + Number(summary.commissionAverageMagic?.[index] || 0) * Number(summary.games || 0), 0);
    return round(total / Math.max(1, totalGames), 4);
  });
}

function mergeSummaryMetrics(summaries) {
  const valid = (summaries || []).filter(Boolean);
  const librarySizeSamples = valid.reduce((sum, summary) => sum + Number(summary.totalTurns || 0), 0);
  return {
    librarySizeSamples,
    librarySizeSum: valid.reduce((sum, summary) =>
      sum + Number(summary.averageLibrarySize || 0) * Number(summary.totalTurns || 0), 0),
    maxLibrarySize: valid.reduce((max, summary) => Math.max(max, Number(summary.maxLibrarySize || 0)), 0),
    prunedMaterialCount: valid.reduce((sum, summary) => sum + Number(summary.prunedMaterialCount || 0), 0),
  };
}

function mergePerformanceSummaries(performanceLists) {
  const map = new Map();
  performanceLists.flat().forEach((row) => {
    if (!row?.stage) return;
    if (!map.has(row.stage)) {
      map.set(row.stage, {
        stage: row.stage,
        label: row.label || PERFORMANCE_STAGE_LABELS[row.stage] || row.stage,
        totalMs: 0,
        samples: 0,
      });
    }
    const item = map.get(row.stage);
    item.totalMs += Number(row.totalMs || 0);
    item.samples += Number(row.samples || 0);
  });
  const totalMs = Array.from(map.values()).reduce((sum, row) => sum + Number(row.totalMs || 0), 0);
  return Array.from(map.values()).map((row) => ({
    stage: row.stage,
    label: row.label,
    totalMs: round(row.totalMs, 4),
    averageMs: round(row.totalMs / Math.max(1, row.samples), 4),
    percent: round(totalMs ? row.totalMs / totalMs * 100 : 0, 2),
    samples: row.samples,
  })).sort((a, b) => b.totalMs - a.totalMs);
}

function toCsvCell(value) {
  const text = String(value ?? "");
  return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

function round(value, precision = 4) {
  if (!Number.isFinite(Number(value))) return 0;
  const unit = Math.pow(10, precision);
  return Math.round(Number(value) * unit) / unit;
}

function clamp(value, min, max) {
  if (!Number.isFinite(Number(value))) return min;
  return Math.min(max, Math.max(min, Number(value)));
}

function getNow() {
  return typeof performance !== "undefined" && performance.now
    ? performance.now()
    : Date.now();
}
